/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Button,
  Collapse,
  useTheme,
  Tooltip,
  Popover,
  MenuItem,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  TextField,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useParams, useMatch } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { m, AnimatePresence } from 'framer-motion';
import {
  closeWhatsappConversationWithReason,
  fetchMoreWhastappMessages,
  fetchWhatsappChatAndOtherMetaData,
  markAsUnRead,
  pinMessage,
  removeWhatsappTag,
  reOpenWhatsappConversationWithReason,
  resetWhatsappChat,
  saveWhatsappChatMetaData,
  sendWhatsappMessage,
  sendWhatsappMediaMessage,
  generateAiResponse,
  clearAiAssistant,
  fetchAIWizardStatus,
  clearServiceWindowClosedDialog,
  reactToMessage,
  revokeMessage,
  starMessage,
  unstarMessage,
  editMessage,
  forwardMessages,
  fetchWhatsappThreadCalls,
} from 'src/redux/slices/whatsappV2';
import { updateError } from 'src/redux/slices/user';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { callApi } from 'src/utils/apiCall';
import {
  formatTemplateName,
  getTemplateBody,
} from 'src/components/whatsappComponents/whatsAppTemplateUtils';
import QuickReplyDraftBubble from 'src/components/whatsappComponents/QuickReplyDraftBubble';
import QuickReplyChip from 'src/components/whatsappComponents/QuickReplyChip';
import {
  isQuickReplyTemplate,
  pickOptimisticContent,
  showPersonalReplyMenuItem,
  isScrollToBottomFabVisible,
} from 'src/components/whatsappComponents/quickReplyUtils';
import FeatureGuard from 'src/guards/FeatureGuard';
import useMessageLongPress from 'src/hooks/useMessageLongPress';
import useScrollToMessage from 'src/hooks/useScrollToMessage';
import useComposerAutocomplete from 'src/hooks/useComposerAutocomplete';
import useThreadDraft, { readDraftForThread } from 'src/hooks/useThreadDraft';
// Same component the super-admin Activity tab and the AI popover use. A
// second copy of the verdict/tag interaction would drift from it on the first
// change — that interaction is where every subtle bug in this feature has lived.
import { piiProps } from 'src/components/DemoBlur';
import {
  arePhoneNumsEqual,
  formatWindowRemaining,
  isWithin24Hours,
  mergeCallEventsIntoMessages,
  parsedDateTimeForWhatsapp,
  parseWhatsappMarkup,
  resolveServiceWindow,
  stringAvatar,
} from './whatsappUtil';
import { getStyles, getAvatarColor, COLORS } from './styles';
import usePersonalReply from './usePersonalReply';
import useMappedTemplateDraft from './useMappedTemplateDraft';
import WhatsappSendingBar from './WhatsappSendingBar';
import SearchLeadForWhatsapp from './SearchLeadForWhatsapp';
import StatusIcon from './WhatsappStatusIcons';
import MessageRating from './MessageRating';

import MediaDisplayComponent from './MediaDisplayComponent';
import AddMediaButtons from './AddMediaButtons';
import QuickActions from './QuickActions';
import WhatsappSkeleton from './WhatsappChatSkeletons';
import WhatsappTemplateModal from './WhatsappTemplatePreview';
import CloseChatPopup from './CloseChatPopup';
import { AIAssistantMenu, AIResultPanel } from './AIAssistant';
import EmojiPickerButton from './EmojiPickerButton';
import FormattingToolbar from './FormattingToolbar';
import MessageActionSheet from './MessageActionSheet';
import EmojiReactionBar from './EmojiReactionBar';
import StarredMessagesPanel from './StarredMessagesPanel';
import PinnedMessageBanner from './PinnedMessageBanner';
import ReEngagementBanner from './ReEngagementBanner';
import WhatsappAIModeControl from './WhatsappAIModeControl';
import MessageInfoDrawer from './MessageInfoDrawer';
import MultiSelectTopBar from './MultiSelectTopBar';
import ForwardPickerDialog from './ForwardPickerDialog';
import ComposerAutocompletePopover from './ComposerAutocompletePopover';
import { searchEmojiShortcodes } from './emojiShortcodes';
import UnreadDivider from './UnreadDivider';
import WhatsappCallControls from './WhatsappCallControls';

// Written by apps/whatsapp/ai_agent/tasks.TEST_MODE_PREFIX. Kept in step by
// hand, and deliberately distinctive: nothing else in the product starts a
// message with it, so a false positive would need somebody to type it.
const TEST_MODE_PREFIX = '[Test mode]';

// F9 — human-readable call duration for inline call-event rows.
// 45 → "45s", 133 → "2m 13s", 3725 → "1h 2m".
const formatCallDuration = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return null;
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) {
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
};

// F9 — label + icon + colour for a call-event timeline row. isMissed is the
// server-computed flag from WhatsappCallSerializer (authoritative).
const describeCallEvent = ({ direction, isMissed, durationSeconds }) => {
  const isInbound = String(direction).toLowerCase() === 'inbound';
  if (isMissed) {
    return {
      label: isInbound ? 'Missed call' : 'Outgoing call · no answer',
      icon: isInbound ? 'mdi:phone-missed' : 'mdi:phone-cancel',
      color: '#ef4444',
    };
  }
  const duration = formatCallDuration(durationSeconds);
  const base = isInbound ? 'Incoming call' : 'Outgoing call';
  return {
    label: duration ? `${base} · ${duration}` : base,
    icon: isInbound ? 'mdi:phone-incoming' : 'mdi:phone-outgoing',
    color: '#54656F',
  };
};

// Helper to parse date using existing utility (handles DD-MM-YYYY format)
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Use existing parsedDateTimeForWhatsapp with returnRawDateTime=true to get MM-DD-YYYY format
    const parsedStr = parsedDateTimeForWhatsapp(dateStr, null, true);
    if (!parsedStr) return null;
    // Clean microseconds before parsing
    const cleanDate = parsedStr.replace(/\.\d{6}Z?$/, '');
    const date = new Date(cleanDate);
    if (!Number.isNaN(date.getTime())) return date;
    return null;
  } catch (error) {
    return null;
  }
};

// Helper to format date for dividers — native-WA style: Today, Yesterday,
// or absolute "12 May 2026" (falls back to full date if older than a week
// to avoid ambiguity around "Monday"-style weekday labels across locales).
const formatDateForDivider = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return null;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMsg = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfMsg) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';

  // Absolute date — include year only if different from current year
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(
    'en-GB',
    sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' }
  );
};

// Helper to get just the date part for comparison
const getDateKey = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return null;
  return date.toDateString();
};

// Human-friendly labels for the mapped-template blocked-state warning. Covers
// both the FE's short context keys AND the raw BACKEND `get_context()` field
// names (apps.leads.models.Lead.get_context / apps.practice.models.
// PracticeContact.get_context on the dentalcrm repo) — a mapped template can
// be authored with either.
const MAPPED_FIELD_LABELS = {
  first_name: 'first name',
  treatment_name: 'treatment',
  practice_name: 'practice name',
  answer: 'message',
  lead_first_name: 'first name',
  lead_last_name: 'last name',
  lead_salutation: 'salutation',
  lead_email: 'lead email',
  lead_phone: 'lead phone number',
  practice_phone: 'practice phone number',
  practice_email: 'practice email',
  practice_whatsapp: 'practice WhatsApp number',
  practice_website: 'practice website',
  practice_booking_url: 'booking link',
  practice_address_line_1: 'practice address',
  practice_address_line_2: 'practice address (line 2)',
  practice_city: 'practice city',
  practice_county: 'practice county',
  practice_postcode: 'practice postcode',
  practice_full_address: 'practice address',
};

const ChatPage = ({
  onToggleDetails,
  detailsOpen,
  embedded,
  // eslint-disable-next-line no-unused-vars
  onOpenBooking,
  onSetChatMessage,
  onOpenResolve,
  hideResolve,
  onMessageSent,
  isMobile,
  isCompact,
  onBack,
  onToggleFullscreen,
  isFullscreen,
  onToggleFocusMode,
  isFocusMode,
  // Task #89 — engagement-template prefill from FollowUpV2 sidebar.
  // When opened against a LEAD_NEXT_ACTION / WHATSAPP_SENT checklist item
  // whose template has a per-practice WhatsApp mapping, FollowUpWhatsappChat
  // forwards the resolved engagement-template id; we fetch its body and
  // pre-load the composer. Mirrors IosEmailComposerBody at
  // src/sections/tasks/composers/IosEmailComposerBody.jsx:813.
  prefillTemplateId,
  prefillPracticeId,
  // Mapped-template draft prefill — the WhatsappMessageTemplate PK (id, matched
  // FIRST) + exact Meta template NAME (fallback), resolved from a checklist
  // WhatsApp item's per-practice mapping (/checklist-whatsapp-mappings/resolve/ →
  // whatsapp_template_id + whatsapp_template_name). We match one in the loaded
  // `currentWhatsappTemplate` list, preview its body as a draft bubble, and send
  // it via the approved-template path (handleQuickReplySend) so it works whether
  // the 24-hour window is open OR closed. Distinct from prefillTemplateId, which
  // fetches an ENGAGEMENT-template BODY into the free composer (open-window only).
  prefillWaTemplateId,
  prefillWaTemplateName,
  // The checklist/task item id the mapped template came from — scopes the
  // mapped-draft "dismissed" flag so two items sharing a template are independent.
  prefillWaItemId,
  // Called after a CONFIRMED successful WA text-message send (response 200).
  // Distinct from onMessageSent which fires optimistically before the API round-trip.
  onMessageConfirmed,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme, { isMobile });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { practiceId } = useParams();
  // Direct-load deep links — nested route param `threadId` from
  // /dashboard/practice/:practiceId/whatsapp/thread/:threadId. `useParams`
  // alone returns only the parent route's params; `useMatch` is the
  // canonical way to read nested params here. Used by the URL → thread
  // backup-fetch effect below.
  const threadRouteMatch = useMatch('/dashboard/practice/:practiceId/whatsapp/thread/:threadId');
  const urlThreadId = threadRouteMatch?.params?.threadId ?? null;
  const [whatsappMessages, setWhatsappMessages] = React.useState([]);
  const [templateModalOpen, setTemplateModalOpen] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [sendMessage, setSendMessage] = React.useState('');
  // Expose setSendMessage to parent for pre-filling chat input
  React.useEffect(() => {
    if (onSetChatMessage) onSetChatMessage(setSendMessage);
  }, [onSetChatMessage]);

  // Task #89 — Prefill composer from a resolved engagement-template id.
  // Fired when FollowUpV2 hands us a templateId (resolved via
  // /checklist-whatsapp-mappings/resolve/). We fetch the engagement-template
  // and seed `sendMessage` with its body. The template may be either a
  // free-form template (use `body` directly) or a synced WhatsApp HSM
  // (`components[].BODY.text`); we try the WA-shape extractor first, then
  // fall back to the engagement-template `body` field. Mirrors the email
  // path in IosEmailComposerBody.jsx:813.
  const prefilledTemplateIdRef = React.useRef(null);
  React.useEffect(() => {
    if (!prefillTemplateId || !prefillPracticeId) return undefined;
    // Guard against re-applying the same template (e.g. when the
    // checklist item re-opens or React re-runs the effect on prop identity
    // churn). The operator may have edited the prefilled body — don't
    // clobber their edits with the same template body twice.
    if (prefilledTemplateIdRef.current === prefillTemplateId) return undefined;
    let active = true;
    callApi(`/api/v1/practices/${prefillPracticeId}/engagement-templates/${prefillTemplateId}/`)
      .then((res) => {
        if (!active) return;
        if (res?.status === 200 && res?.data) {
          const fromComponents = getTemplateBody(res.data);
          const body = fromComponents || res.data.body || '';
          if (body) {
            prefilledTemplateIdRef.current = prefillTemplateId;
            setSendMessage(body);
          }
        }
      })
      .catch((err) => {
        // Silent failure — composer stays on its current value (likely empty
        // or the persisted draft). We don't surface a toast because this is a
        // best-effort UX prefill, not a user-initiated action.
        // eslint-disable-next-line no-console
        console.error('ChatPage: template prefill fetch failed', err);
      });
    return () => { active = false; };
  }, [prefillTemplateId, prefillPracticeId]);
  const [isUserNearBottom, setIsUserNearBottom] = React.useState(true);
  // Count of messages that arrived while the user was scrolled up — surfaced
  // on the scroll-to-bottom FAB as a small green badge. Resets to 0 when the
  // user scrolls back to the bottom or clicks the FAB.
  const [unreadWhileScrolled, setUnreadWhileScrolled] = React.useState(0);
  // Bubble action sheet — anchor carries { anchorEl, msg }. Driven by
  // useMessageLongPress so right-click (desktop), long-press (touch),
  // and shift+click (keyboard) all funnel through one activation path.
  const [bubbleMenu, setBubbleMenu] = React.useState(null);
  // Delete-for-everyone confirmation. When set, holds the message we're
  // about to revoke so the dialog can render against a closed action sheet.
  const [revokeTarget, setRevokeTarget] = React.useState(null);
  // Track B — Star/Pin UI state.
  const [starredPanelOpen, setStarredPanelOpen] = React.useState(false);
  const [replacePinTarget, setReplacePinTarget] = React.useState(null);
  const [showMessageActionHint, setShowMessageActionHint] = React.useState(false);
  // Track C / F6 inline edit. Shape: { wamid, draft } | null.
  // We key off wamid (Meta's stable id, present on both optimistic and
  // server-confirmed messages once Meta acks the send) instead of
  // msg.id, because the optimistic-send path uses Date.now() as a
  // placeholder id and the slice replaces it with the real DB PK on
  // confirmation — keying off id meant the inline TextField stopped
  // rendering the moment the server response landed. wamid survives
  // that swap. Edit is gated on wamid presence (un-acked optimistic
  // messages have no wamid yet — and the BE rejects edit on those).
  const [editingBubble, setEditingBubble] = React.useState(null);
  // Track C / F8 message info — { messageId, threadId } | null.
  const [messageInfoTarget, setMessageInfoTarget] = React.useState(null);
  // Reply-to-quote: when set, the composer renders a quote preview card
  // above the textarea and the next outbound send carries `replied_to_wamid`
  // so Meta posts the message as a threaded reply on the patient's device.
  // Shape: { wamid, body, fromWaid, isFromMe } | null.
  const [replyingTo, setReplyingTo] = React.useState(null);
  const [isWindowExpired, setIsWindowExpired] = React.useState(false);
  // Re-render the window pill once a minute so the "5h 12m" countdown
  // ticks down without needing the operator to re-open the thread.
  const [, setWindowTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setWindowTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const [openCloseConversation, setOpenCloseConversation] = React.useState(false);
  // closeReason state removed — managed inside CloseChatPopup now
  const [quickCloseAnchor, setQuickCloseAnchor] = React.useState(null);
  const [toolbarMenuAnchor, setToolbarMenuAnchor] = React.useState(null);
  const [composerToolsAnchor, setComposerToolsAnchor] = React.useState(null);
  const [aiMenuOpen, setAiMenuOpen] = React.useState(false);
  const [aiMenuAnchor, setAiMenuAnchor] = React.useState(null);
  const [lastAiAction, setLastAiAction] = React.useState(null);
  const [lastAiContext, setLastAiContext] = React.useState(null);
  const [expandedTemplates, setExpandedTemplates] = React.useState(new Set());
  const [attachmentFile, setAttachmentFile] = React.useState(null);
  const [attachmentPreview, setAttachmentPreview] = React.useState(null);
  const [attachmentCaption, setAttachmentCaption] = React.useState('');
  // Attachment "+" popover — native-WA plus-button grid. Anchor drives open/close.
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = React.useState(null);
  // Tier 2 / F-16 — drag-and-drop overlay.
  // We track drag-enter / drag-leave with a counter rather than a
  // boolean because dragenter/dragleave fire for every child element
  // the cursor crosses; a naive boolean toggles off as soon as the
  // cursor enters a nested div.
  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragCounterRef = React.useRef(0);
  // Tier 2 / F-16 — multi-file drop queue. We process drops one at
  // a time through the existing single-file send path so the
  // operator can review caption + cancel each one.
  const [pendingDropQueue, setPendingDropQueue] = React.useState([]);
  const inputRef = React.useRef(null);
  const scrollRef = React.useRef();
  const fileInputRef = React.useRef(null);
  const quickActionsRef = React.useRef({});
  const attachmentPreviewRef = React.useRef(null);
  // Tracks message IDs that were present at initial render of the current
  // chat. Any received message whose id is NOT in this set when rendered
  // is treated as "newly arrived" and gets the subtle slide-in animation.
  // Reset whenever the chat thread identity changes.
  const seenMessageIdsRef = React.useRef(new Set());
  const seededForThreadRef = React.useRef(null);
  // Tier 3 / F-33 — first-unread divider.
  // Snapshot the unread count when the thread is FIRST opened so the
  // divider's position doesn't shift after `mark_as_read` zeroes the
  // server-side counter. The snapshot resets whenever the thread
  // identity changes (re-open / refresh) — matching the spec
  // "divider stays visible until thread is reopened/refreshed".
  const unreadSnapshotForThreadRef = React.useRef({ threadId: null, count: 0 });
  const [unreadDividerCount, setUnreadDividerCount] = React.useState(0);
  // True when the unread divider element is currently inside the
  // scrollable viewport — drives the floating "N unread ↓" jump button
  // visibility. Defaults to false (show button) until the IO observer
  // reports a state.
  const [isUnreadDividerVisible, setIsUnreadDividerVisible] = React.useState(false);
  const unreadDividerRef = React.useRef(null);
  // F6: tracks when the UnreadDivider DOM node mounts/unmounts so the
  // IntersectionObserver effect can re-establish itself. We need this
  // because the IO effect runs when unreadDividerCount changes, but
  // at that exact moment the divider element may not have rendered yet
  // (messages are fetched async). Without a remount signal the IO never
  // fires and the pill never hides. Using a callback ref (which sets
  // both the imperative ref for scrollDividerIntoView AND this state
  // flag for the IO dep array) avoids the TDZ constraint that blocks
  // referencing unreadDividerIndex (declared later in the component)
  // inside a dep array defined here.
  const [unreadDividerMounted, setUnreadDividerMounted] = React.useState(false);
  const unreadDividerCallbackRef = React.useCallback((node) => {
    unreadDividerRef.current = node;
    setUnreadDividerMounted(!!node);
  }, []);
  // --- Mobile keyboard resilience ----------------------------------------
  // Mirrors mobileFullScreenPanel (styles.js:1989-2003) which uses height:100dvh.
  // On mobile, window.visualViewport.height shrinks when the on-screen keyboard
  // opens; binding the container root to that height keeps the composer footer
  // in view instead of scrolling under the keyboard. No-op on desktop (guarded
  // by isMobile prop; also no-op if visualViewport API is absent).
  const [viewportH, setViewportH] = React.useState(null);
  React.useEffect(() => {
    if (!isMobile) return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      // offsetTop > 0 on iOS when the page has scrolled upward; adding it back
      // gives the true "top of visible area → bottom of visible area" height.
      setViewportH(vv.height + vv.offsetTop);
      // Column-reverse container: scrollTop === 0 is pinned to the latest
      // message. Snap back so the thread bottom stays visible after the
      // keyboard opens / closes.
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isMobile]);

  // --- Swipe-to-reply gesture state (mobile only) -----------------------
  // Tracks the in-flight drag for a single bubble. We bypass React state
  // during the drag itself — onTouchMove fires up to 60×/s and re-rendering
  // the entire chat list each frame would jank. Instead we mutate the
  // dragged bubble's wrapper transform directly via DOM and only commit
  // the reply via setReplyingTo once the gesture completes past threshold.
  // Shape:
  //   { id, startX, startY, deltaX, wrapperEl, indicatorEl, msg, cancelled }
  // null when no drag is active.
  const swipeStateRef = React.useRef(null);

  const {
    isLoading,
    whatsAppChatMetaData,
    whatsappChats,
    whatsAppChatNextAPI,
    whatsappChatScreenLoading,
    whatsappChatNextAPILoading,
    whatsppSendLoading,
    currentWhatsappTemplate,
    allowedUsageTypes,
    aiAssistantLoading,
    aiAssistantResult,
    aiAssistantError,
    aiAssistantActionType,
    practiceChoices,
    serviceWindowClosedDialog,
    currentBusinessId,
    currentWhatsappPhoneId,
    currentBusinessIsConnected,
    threadCallEvents,
    threadCallEventsThreadId,
    whatsappCallsById,
  } = useSelector(
    (state) => ({
      isLoading: state?.whatsappV2?.isLoading,
      whatsAppChatMetaData: state?.whatsappV2?.whatsAppChatMetaData,
      whatsappChats: state?.whatsappV2?.whatsappChats,
      whatsAppChatNextAPI: state?.whatsappV2?.whatsAppChatNextAPI,
      whatsappChatScreenLoading: state?.whatsappV2?.whatsappChatScreenLoading,
      whatsappChatNextAPILoading: state?.whatsappV2?.whatsappChatNextAPILoading,
      whatsppSendLoading: state?.whatsappV2?.whatsppSendLoading,
      currentWhatsappTemplate: state?.whatsappV2?.currentWhatsappTemplate,
      allowedUsageTypes: state?.whatsappV2?.allowedUsageTypes,
      aiAssistantLoading: state?.whatsappV2?.aiAssistantLoading,
      aiAssistantResult: state?.whatsappV2?.aiAssistantResult,
      aiAssistantError: state?.whatsappV2?.aiAssistantError,
      aiAssistantActionType: state?.whatsappV2?.aiAssistantActionType,
      practiceChoices: state?.whatsappV2?.practiceChoices,
      serviceWindowClosedDialog: state?.whatsappV2?.serviceWindowClosedDialog,
      currentBusinessId: state?.whatsappV2?.currentBusinessId,
      currentWhatsappPhoneId: state?.whatsappV2?.currentWhatsappPhoneId,
      currentBusinessIsConnected: state?.whatsappV2?.currentBusinessIsConnected,
      // F9 — call events for the open thread (inline timeline rows).
      threadCallEvents: state?.whatsappV2?.threadCallEvents,
      threadCallEventsThreadId: state?.whatsappV2?.threadCallEventsThreadId,
      whatsappCallsById: state?.whatsappV2?.whatsappCallsById,
    }),
    shallowEqual
  );

  // Filter templates by allowed usage types from practice choices config
  const filteredTemplates = React.useMemo(() => {
    if (!allowedUsageTypes || allowedUsageTypes.length === 0) return currentWhatsappTemplate;
    return (currentWhatsappTemplate || []).filter((t) => allowedUsageTypes.includes(t.usage_type));
  }, [currentWhatsappTemplate, allowedUsageTypes]);

  // Template list for the normal pickers (template modal + "/" autocomplete).
  // Personal Reply (QUICK_REPLY) templates are reserved for the closed-window
  // composer and must never appear in these pickers — including the case where a
  // practice has no allowedUsageTypes filter (which would otherwise let them
  // through filteredTemplates).
  const templatesForPicker = React.useMemo(
    () => (filteredTemplates || []).filter((t) => !isQuickReplyTemplate(t)),
    [filteredTemplates]
  );

  // Check if AI is enabled — reads from Redux directly
  const aiWizardConfig = useSelector((state) => state?.whatsappV2?.aiWizardConfig);
  const isAiEnabled = Boolean(aiWizardConfig && aiWizardConfig.enableAiForWhatsapp !== false);

  // The practice this chat/popup belongs to. `practiceId` comes from the URL
  // route (`/dashboard/practice/:practiceId/whatsapp/...`), which is EMPTY on
  // routes that render ChatPage without that segment — e.g. the Follow-up V2
  // popup opened from `/dashboard/tables/new-lead-table`. `prefillPracticeId`
  // is the practice explicitly handed to the popup by its caller
  // (FollowUpWhatsappChat → FollowUpV2/ChecklistItemRow) and is always
  // correct when present, so it takes priority; the route is only a fallback
  // for surfaces that don't pass it. Using the route alone here was the root
  // cause of the New-Lead-table mapped-template false "missing practice
  // name" block (Sentry/RCA 2026-09).
  const effectivePracticeId = prefillPracticeId || practiceId;

  // Practice name — used for Personal Reply (quick-reply) context auto-fill.
  const practiceName = useSelector((state) => {
    const list = state?.practiceTreatmentList?.practiceTreatment?.results;
    const current = Array.isArray(list) ? list.find((p) => String(p.id) === String(effectivePracticeId)) : null;
    return current?.name || '';
  });

  // Tier 2 / F-22 — merge tags. The dashboard layout pre-fetches the
  // global tag list on mount, but `/api/v1/utils/mergetags/` can fail
  // (network blip, 4xx for some users) leaving Redux state at the
  // initial empty array — in which case '@' would otherwise produce
  // zero suggestions. Treat the hardcoded list as the always-available
  // baseline and merge any real fetched tags on top, so operators get
  // a useful popover regardless of whether the fetch succeeded.
  const mergeTagKeys = useSelector((state) => state?.mergeTags?.mergeTagKeys, shallowEqual);
  const fallbackMergeTags = React.useMemo(
    () => [
      'first_name',
      'last_name',
      'practice',
      'practice_name',
      'lead_id',
      'appointment_date',
      'appointment_time',
      'treatment',
    ],
    []
  );
  const availableMergeTags = React.useMemo(() => {
    const real = Array.isArray(mergeTagKeys) ? mergeTagKeys : [];
    // De-dupe via Set so the baseline keys don't double up if the
    // backend list happens to include them. Sort for stable display
    // order in the autocomplete popover.
    return [...new Set([...fallbackMergeTags, ...real])].sort();
  }, [mergeTagKeys, fallbackMergeTags]);

  // GDPR FIX: Fail-secure check for lead communication status
  // If thread has a linked lead, we MUST have leadDetails loaded before allowing send
  // This prevents bypassing GDPR checks via race conditions during chat load
  const threadHasLead = Boolean(whatsAppChatMetaData?.thread?.lead?.id);
  const leadDetailsLoaded = Boolean(whatsAppChatMetaData?.leadDetails?.id);
  const isLeadOpenForCommunication = whatsAppChatMetaData?.leadDetails?.is_open_for_communication !== false;
  const hasThreadLoaded = Boolean(whatsAppChatMetaData?.thread?.id) && !whatsappChatScreenLoading;
  const hasLinkedLead = hasThreadLoaded && Boolean(whatsAppChatMetaData?.leadDetails?.id);
  const canShowUnlinkedThreadActions = hasThreadLoaded && !hasLinkedLead;

  // Fail-secure: Disable send if thread has lead but leadDetails isn't loaded yet
  const isSendAllowed = threadHasLead ? leadDetailsLoaded && isLeadOpenForCommunication : true;
  const isComposerDisabled =
    whatsappChatScreenLoading ||
    whatsappChats === undefined ||
    whatsappChats === null ||
    whatsppSendLoading ||
    currentBusinessIsConnected !== true ||
    !isSendAllowed;
  const canSendComposerMessage = sendMessage.trim().length > 0 && !isComposerDisabled;

  // Closed-window Personal Reply — reuses the native input. The typed text
  // (`sendMessage`) is the personalised message = the template's {{answer}}; the
  // hook owns the greeting + sign-off picks and resolves the exact template.
  const personalReply = usePersonalReply({
    // Feed the RAW template list (not filteredTemplates) so QUICK_REPLY bypasses
    // the practice `allowedUsageTypes` allowlist — Personal Reply is a reserved
    // composer type and must appear regardless of a practice's usage-type filter.
    // The hook's own filterQuickReplyTemplates then narrows to the quick_reply set.
    templates: currentWhatsappTemplate,
    leadDetails: whatsAppChatMetaData?.leadDetails,
    practiceName,
    answer: sendMessage,
  });
  // Mapped-template draft — a checklist WhatsApp item resolved a per-practice
  // mapped template ({ id, name }); the hook matches it (id-first) in the loaded
  // list and builds a fully-merged draft + send-context + blocked/missing-field
  // state. Sent via the approved-template path so it works open OR closed.
  const mappedTemplate = useMappedTemplateDraft({
    templates: currentWhatsappTemplate,
    templateId: prefillWaTemplateId,
    templateName: prefillWaTemplateName,
    itemId: prefillWaItemId,
    leadDetails: whatsAppChatMetaData?.leadDetails,
    practiceName,
    // practiceId/leadId drive the hook's own fetch of the FULL backend
    // get_context() payloads (practice + lead) so the pre-send check covers
    // every backend field, not just the short-alias hints above. Passing
    // practiceId here is what lets the hook resolve `practice_phone` and
    // every other practice/lead field the BE can fill.
    practiceId: effectivePracticeId,
    leadId: whatsAppChatMetaData?.leadDetails?.id,
  });
  const mappedTemplateDraft = mappedTemplate.draft;
  // Closed-window composer is "+"-menu driven. Personal Reply auto-opens on a
  // fresh closed thread; the "+" menu re-opens it or opens the native Templates
  // picker. `personalReplyActive` = the greeting/sign-off + native-input flow is
  // live (open AND there are sign-off templates).
  const [personalReplyOpen, setPersonalReplyOpen] = React.useState(true);
  const [closedToolsAnchor, setClosedToolsAnchor] = React.useState(null);
  // A pinned mapped-template draft takes precedence — hide the Personal Reply
  // greeting/sign-off chips while the mapped template is the primary CTA.
  const personalReplyActive = personalReplyOpen && personalReply.hasOptions && !mappedTemplateDraft;
  const closedThreadId = whatsAppChatMetaData?.thread?.id ?? null;
  // Auto-open Personal Reply whenever the thread changes (so a fresh closed
  // thread lands in Personal Reply mode).
  React.useEffect(() => {
    setPersonalReplyOpen(true);
  }, [closedThreadId]);

  // Send is allowed only while Personal Reply is active and the message passes the
  // same validation (640 cap, no newline/tab/4+space) + the shared composer guard.
  const canSendPersonalReply =
    personalReplyActive && !personalReply.answerError && !isComposerDisabled;
  // Native send/Enter (closed window) → send the resolved template
  // (quick_reply_<signoff>[_tod]) via the existing template path — NOT free text.
  // On success: clear the field, reset the picks, and COLLAPSE Personal Reply.
  const handlePersonalReplySend = () => {
    if (!canSendPersonalReply || !personalReply.selectedTemplate) return;
    handleQuickReplySend(personalReply.selectedTemplate.name, personalReply.buildSendContext(), {
      onSuccess: () => {
        setSendMessage('');
        personalReply.reset();
        setPersonalReplyOpen(false);
      },
    });
  };

  // ── Mapped-template draft (state owned by useMappedTemplateDraft) ───────────
  const handleDismissMappedTemplate = () => mappedTemplate.dismiss();
  // Send the mapped template via the approved-template path (never free text).
  // BLOCKED guard: refuse to send when required merge fields are unresolved
  // (would go out as blank "-") — the send bar surfaces the warning instead.
  const handleMappedTemplateSend = () => {
    if (!mappedTemplateDraft || mappedTemplateDraft.blocked || isComposerDisabled) return;
    handleQuickReplySend(mappedTemplateDraft.name, mappedTemplateDraft.sendContext, {
      // Mapped templates have no free-text answer — seed the optimistic bubble
      // with the rendered draft body so it isn't blank while sending.
      optimisticContent: mappedTemplateDraft.preview?.before || '',
      onSuccess: () => mappedTemplate.markSent(),
    });
  };

  // 24-hour Meta service window — server-aware. The thread serializer
  // exposes ``service_window_open`` and ``service_window_closes_at``;
  // ``resolveServiceWindow`` falls back to the legacy client-side check
  // off the last received bubble's timestamp if those fields aren't
  // present (older API response). Re-evaluated whenever the thread or
  // the minute-tick changes so the "5h 12m" countdown stays current.
  const serviceWindow = React.useMemo(
    () => resolveServiceWindow(whatsAppChatMetaData?.thread),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      whatsAppChatMetaData?.thread?.service_window_open,
      whatsAppChatMetaData?.thread?.service_window_closes_at,
      whatsAppChatMetaData?.thread?.last_inbound_at,
    ]
  );
  // The thread is the one object EVERY entry path loads, so read close state
  // off it first and treat `isCloseObject` as the (equally valid) shape the
  // Contacts / thread-URL paths assemble. Metadata-only made the Resolved pill
  // and the Reopen button depend on which screen opened the chat.
  const conversationClosed = Boolean(
    whatsAppChatMetaData?.thread?.is_closed ?? whatsAppChatMetaData?.isCloseObject?.isClosed
  );
  const closeReason = whatsAppChatMetaData?.thread?.metadata?.close_reason
    || whatsAppChatMetaData?.isCloseObject?.closedReason
    || 'Closed';
  const unreadCount = whatsAppChatMetaData?.thread?.unread_count || 0;
  const pinnedMessage = whatsAppChatMetaData?.thread?.pinned_message;
  const starredCount = React.useMemo(
    () => (whatsappMessages || []).filter((message) => message?.isStarred).length,
    [whatsappMessages]
  );
  const messageCount = whatsappMessages?.length || 0;
  const hasNoMessages = hasThreadLoaded && !whatsappChatScreenLoading && messageCount === 0;
  const serviceWindowLabel = serviceWindow.open
    ? `Free messaging ${serviceWindow.msRemaining != null ? formatWindowRemaining(serviceWindow.msRemaining) || 'open' : 'open'}`
    : 'Window closed';
  const serviceWindowTooltip = (() => {
    if (!serviceWindow.open) {
      return 'The 24-hour Meta window is closed. Send an approved template to restart the conversation.';
    }
    if (serviceWindow.closesAt) {
      return `Free-form and utility messages are available until ${serviceWindow.closesAt.toLocaleString()}`;
    }
    return 'Free-form and utility messages are available inside the 24-hour Meta window.';
  })();
  React.useEffect(() => {
    if (!hasThreadLoaded || whatsappChatScreenLoading || messageCount === 0) return undefined;
    const storageKey = 'wa-message-action-hint-v1';
    if (window.localStorage.getItem(storageKey) === 'seen') return undefined;
    setShowMessageActionHint(true);
    const timer = window.setTimeout(() => {
      setShowMessageActionHint(false);
      window.localStorage.setItem(storageKey, 'seen');
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [hasThreadLoaded, messageCount, whatsappChatScreenLoading]);

  const dismissMessageActionHint = React.useCallback(() => {
    window.localStorage.setItem('wa-message-action-hint-v1', 'seen');
    setShowMessageActionHint(false);
  }, []);

  // Tier 2 / F-18 — per-thread draft persistence (localStorage).
  // When the thread changes, we hydrate the composer from the saved
  // draft for that thread (or clear it if none). Subsequent edits
  // are pushed back into the hook on every change so the next
  // thread switch / refresh restores them. The hook itself debounces
  // localStorage writes so we don't thrash on every keystroke.
  const threadIdForDraft = whatsAppChatMetaData?.thread?.id ?? null;
  const { setDraft: persistDraft, clearDraft: clearPersistedDraft } = useThreadDraft(threadIdForDraft);
  const lastHydratedThreadRef = React.useRef(undefined);
  React.useEffect(() => {
    // Only re-hydrate when the thread identity actually changes.
    // Otherwise every keystroke would race the debounced save and
    // overwrite live typing with stale storage state.
    //
    // Read directly from localStorage (not the hook's `draft` state)
    // because the hook's re-hydration useEffect is queued to run
    // AFTER this parent effect — reading the hook's state here would
    // see the previous thread's draft value. The exported helper
    // gives us the freshly-stored value synchronously.
    if (lastHydratedThreadRef.current !== threadIdForDraft) {
      lastHydratedThreadRef.current = threadIdForDraft;
      setSendMessage(readDraftForThread(threadIdForDraft) || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadIdForDraft]);

  // ---- AI draft, in the composer -------------------------------------
  //
  // The AI's suggested reply used to live in a popover at the top of the
  // screen, so the operator read it up there and typed their reply down here.
  // It now arrives in the composer itself: the box where a reply gets written
  // is the box the draft appears in.
  //
  // `aiComposer` is published by WhatsappAIModeControl and carries the turn id
  // plus the two calls that resolve it. Sending has to go back through
  // `send-draft` rather than the ordinary send: that is what marks the turn
  // SENT and attributes the message to the AI. A plain send would post the same
  // words as a human reply and leave the turn PENDING forever.
  const [aiComposer, setAiComposer] = React.useState(null);
  const [aiDraftLoaded, setAiDraftLoaded] = React.useState(null);
  const [aiDraftSending, setAiDraftSending] = React.useState(false);
  const aiComposerRef = React.useRef(null);
  const aiDraftLoadedRef = React.useRef(null);
  React.useEffect(() => { aiComposerRef.current = aiComposer; }, [aiComposer]);
  React.useEffect(() => { aiDraftLoadedRef.current = aiDraftLoaded; }, [aiDraftLoaded]);

  // Identity only, so a re-publish of the same draft does not re-type over an
  // operator who is halfway through editing it.
  const aiDraftId = aiComposer?.draft?.id ?? null;
  const aiDraftText = aiComposer?.draft?.text || '';
  // What is in the box right now, readable from an effect that does not have
  // `sendMessage` in its deps. It has to be a ref: deciding inside the
  // setSendMessage updater looked equivalent and was not — the decision has to
  // be visible OUTSIDE the updater, because whether we inserted is what says
  // whether the words in the box are the AI's or the operator's, and that is
  // what decides which endpoint Send goes to.
  const sendMessageRef = React.useRef('');
  React.useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  React.useEffect(() => {
    if (!aiDraftId) return;
    if (aiDraftLoadedRef.current?.id === aiDraftId) return;
    // Never clobber something a person typed. If they were already writing when
    // the draft landed, it waits in the popover instead — losing an operator's
    // half-written sentence is worse than making them click for the draft.
    //
    // And when we do not insert, `aiDraftLoaded` must stay unset. Setting it
    // anyway would mark the composer as holding the AI's reply while it held
    // the operator's, so pressing Send would post THEIR words through
    // `send-draft` — recorded as the AI's message, resolving the AI's turn.
    const current = sendMessageRef.current;
    const previous = aiDraftLoadedRef.current?.text;
    if (current && current.trim() && current !== previous) return;

    setSendMessage(aiDraftText);
    setAiDraftLoaded({ id: aiDraftId, text: aiDraftText });
    persistDraft(aiDraftText);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const end = el.value?.length ?? 0;
      try { el.setSelectionRange(end, end); } catch { /* not focusable yet */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDraftId, aiDraftText]);

  // The draft went, or was discarded elsewhere, or the thread changed. Take the
  // AI's words back out of the box — but only if they are still the AI's words.
  React.useEffect(() => {
    if (aiDraftId) return;
    const loaded = aiDraftLoadedRef.current;
    if (!loaded) return;
    setAiDraftLoaded(null);
    setSendMessage((prev) => (prev === loaded.text ? '' : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDraftId]);

  React.useEffect(() => {
    setAiDraftLoaded(null);
    setAiDraftSending(false);
  }, [threadIdForDraft]);

  // Emptying the box releases the AI's claim on it. Editing a draft is the
  // whole point of Draft mode, so any amount of rewriting still sends through
  // `send-draft` — but clearing it and starting again is a person writing their
  // own reply, and that must not be published as the AI's words or resolve the
  // AI's turn. The draft itself is untouched: it is still offered in the
  // popover, with its Discard button, until somebody decides about it.
  React.useEffect(() => {
    if (!aiDraftLoaded) return;
    if (sendMessage.trim()) return;
    setAiDraftLoaded(null);
  }, [sendMessage, aiDraftLoaded]);

  // True while the composer is holding an AI draft the operator has not
  // replaced. Once they have rewritten it past recognition it is their message,
  // and it sends the ordinary way.
  const composerHoldsAiDraft = !!aiDraftLoaded && !!aiComposer?.draft
    && aiComposer.draft.id === aiDraftLoaded.id;

  const discardAiDraft = React.useCallback(() => {
    const current = aiComposerRef.current;
    const loaded = aiDraftLoadedRef.current;
    setSendMessage((prev) => (loaded && prev === loaded.text ? '' : prev));
    setAiDraftLoaded(null);
    if (current?.discard) current.discard();
  }, []);

  // Tier 2 / F-20, F-21, F-22 — composer autocomplete state.
  const composerAutocomplete = useComposerAutocomplete();

  // F9 — load call events whenever the open thread changes. Also refetch when
  // a live call update lands via socket (whatsappCallsById) so a just-ended or
  // just-missed call appears inline without reopening the thread.
  const activeThreadIdForCalls = whatsAppChatMetaData?.thread?.id ?? null;
  React.useEffect(() => {
    if (!activeThreadIdForCalls) return;
    dispatch(fetchWhatsappThreadCalls(activeThreadIdForCalls));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadIdForCalls, whatsappCallsById, dispatch]);

  React.useEffect(() => {
    // Don't process until we have actual data loaded (not during loading state)
    if (whatsappChatScreenLoading || whatsappChats === undefined || whatsappChats === null) {
      setWhatsappMessages([]);
      setIsWindowExpired(false);
      return;
    }

    setIsUserNearBottom(true);
    const lastRecievedMessage = {};
    const messages = whatsappChats?.flatMap((message, index) => {
      const content = message?.body;
      const type = arePhoneNumsEqual(message?.from_waid, whatsAppChatMetaData?.phoneNumber) ? 'received' : 'sent';
      const id = message?.id;
      const timestamp = parsedDateTimeForWhatsapp(message?.created_at, message?.created_at_human);
      const updatedTimestamp = parsedDateTimeForWhatsapp(message?.updated_at, message?.created_at_human);
      const isMessageInteractive = message?.message_type === 'interactive';
      const flowResponseEmail = message?.flow_response?.email;
      const flowResponseTreatment = message?.flow_response?.treatment;
      const flowResponseComment = message?.flow_response?.comment;
      const isSpecialTemplateMessage =
        message?.message_type === 'template' &&
        message?.template?.name?.length &&
        !message?.template?.components?.[0]?.example;
      const emoji = message?.whatsapp_message_reaction?.emoji || null;
      const timerTime = parsedDateTimeForWhatsapp(message?.created_at, '', true);
      const status = message?.status;
      const mediaMessage = message?.whatsapp_message_media;
      const createdAt = message?.created_at;

      if (type === 'received' && !lastRecievedMessage?.id) {
        lastRecievedMessage.time = timerTime;
        lastRecievedMessage.createdAt = createdAt;
        lastRecievedMessage.id = true;
      }
      // Reply-to-quote: surface the quoted message as a small card we
      // render INSIDE the bubble. The backend serializer hands us a
      // tiny block { id, wamid, body, message_type, from_waid }; the
      // frontend infers ``isFromMe`` by comparing from_waid against the
      // patient's wa_id (same as the bubble-side type detection above).
      let repliedTo = null;
      if (message?.replied_to) {
        const r = message.replied_to;
        repliedTo = {
          id: r?.id ?? null,
          wamid: r?.wamid ?? null,
          body: r?.body || '',
          messageType: r?.message_type || 'text',
          fromWaid: r?.from_waid || '',
          // True when the quoted message was sent BY us (the practice).
          // Mirrors how ``type`` is derived for the parent loop above.
          isFromMe: !arePhoneNumsEqual(r?.from_waid, whatsAppChatMetaData?.phoneNumber),
        };
      }
      const returnObj = {
        id,
        wamid: message?.wamid || null,
        fromWaid: message?.from_waid || null,
        timestamp,
        isMessageInteractive,
        flowResponseEmail,
        flowResponseComment,
        flowResponseTreatment,
        isSpecialTemplateMessage,
        emoji,
        timerTime,
        content: message?.is_revoked ? null : content,
        type,
        updatedTimestamp,
        status,
        mediaMessage,
        mediaMessageType: message?.message_type,
        createdAt,
        isEdited: message?.is_edited || false,
        isRevoked: message?.is_revoked || false,
        isStarred: message?.is_starred || false,
        // Track D / W-1 — forward provenance. BE serializer emits
        // `forwarded_from` for any message Meta (or our own
        // forwardMessages thunk) marked as a forward. Falsy when
        // the message is original.
        forwardedFrom: message?.forwarded_from ?? null,
        // The AI agent composed and sent this one. Not `is_automated`, which is
        // also true for workflows, campaigns and sequences — the backend
        // derives this from an agent turn pointing at the message, so it means
        // "these are the model's words", which is the thing an operator
        // reading back a Live conversation actually needs to tell apart.
        sentByAi: message?.sent_by_ai || false,
        // The turn behind this message and the viewer's own verdict on it, so
        // the thumbs can live on the reply instead of in a panel that only ever
        // offered the most recent one.
        aiRating: message?.ai_rating || null,
        // An approved template, not words the model wrote. The AI chose it and
        // sent it; the practice wrote it and Meta approved it. Labelling the two
        // the same way defeats the point of labelling either.
        aiTemplate: message?.template || null,
        repliedTo,
      };
      if (index === 0 && type === 'received' && !mediaMessage) return returnObj;
      if (index === 0 && type === 'received' && mediaMessage)
        return [
          {
            id: `System${message?.id}media`,
            type: 'system',
            content: '',
            timerTime,
            specialType: 'addMediaButtons',
          },
          returnObj,
        ];
      if (isSpecialTemplateMessage && mediaMessage)
        return [
          {
            id: `System${message?.id}media`,
            type: 'system',
            content: '',
            timerTime,
            specialType: 'addMediaButtons',
          },
          returnObj,
        ];
      if (mediaMessage)
        return [
          {
            id: `System${message?.id}media`,
            type: 'system',
            content: '',
            timerTime,
            specialType: 'addMediaButtons',
          },
          returnObj,
        ];
      return returnObj;
    });

    let windowExpired = false;
    if (messages?.length === 0) {
      messages.push({
        id: `System${Date.now()}`,
        type: 'system',
        content: 'Please select a template to start a conversation.',
        freeze: true,
      });
      windowExpired = true;
    } else if (!lastRecievedMessage?.id) {
      messages?.unshift({
        id: `System${Date.now()}`,
        type: 'system',
        content: 'Please select a template to continue the conversation.',
        freeze: true,
      });
      windowExpired = true;
      // isWithin24Hours expects the raw API timestamp (parsed as UK-local
      // wall-clock via parseApiDateTime) — use `createdAt` (the untouched
      // `message.created_at`), not `.time` (the DD-MM->MM-DD reformatted
      // string used only for the retired legacy-format display path).
    } else if (lastRecievedMessage?.id && !isWithin24Hours(lastRecievedMessage?.createdAt)) {
      messages?.unshift({
        id: `System${Date.now()}windowAlert`,
        type: 'windowAlert',
        startTime: lastRecievedMessage?.createdAt,
      });
      messages?.unshift({
        id: `System${Date.now()}templateRequired`,
        type: 'templateRequired',
      });
      windowExpired = true;
    }

    // Server-aware override: when the backend exposes ``service_window_open``
    // trust it in BOTH directions over the client-side estimate.
    //   closed (false) → Meta will reject free-form delivery; force template.
    //   open  (true)   → server confirms 24 h window is active; clear any
    //                    client-side false-negative (timezone/parse edge) that
    //                    wrongly showed the "select a template" UI when the
    //                    window was actually still valid.
    //
    // Exception: if expiry was triggered by "no messages at all" (cases 1/2
    // above), keep it — a template is required to initiate a conversation
    // regardless of what the service-window flag says.
    if (
      whatsAppChatMetaData?.thread &&
      typeof whatsAppChatMetaData.thread.service_window_open === 'boolean'
    ) {
      if (!whatsAppChatMetaData.thread.service_window_open) {
        windowExpired = true;
      } else if (lastRecievedMessage?.id) {
        // Server confirms window open and there IS a prior received message →
        // override a timestamp-based false-negative from the client check above.
        windowExpired = false;
      }
    }

    // F9 — interleave call events into the conversation timeline.
    // #27: extracted to whatsappUtil.mergeCallEventsIntoMessages so the splice
    // logic (ordering, attached-system gluing, stale-thread guard) is unit
    // tested. Mutates `messages` in place, same as the previous inline block.
    mergeCallEventsIntoMessages(messages, threadCallEvents, {
      activeThreadId: whatsAppChatMetaData?.thread?.id ?? null,
      eventsThreadId: threadCallEventsThreadId,
    });

    setIsWindowExpired(windowExpired);
    setWhatsappMessages(messages);

    // Seed the "already seen" id set on the first population for the
    // current thread so the initial render doesn't animate every
    // message — only subsequent arrivals do. Subsequent effect runs
    // for the same thread leave newly-arrived ids unseen until the
    // post-render commit effect marks them.
    const threadKey = whatsAppChatMetaData?.phoneNumber || whatsAppChatMetaData?.thread?.id || null;
    if (threadKey && seededForThreadRef.current !== threadKey) {
      seenMessageIdsRef.current = new Set((messages || []).map((m) => m?.id).filter(Boolean));
      seededForThreadRef.current = threadKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    whatsAppChatMetaData?.phoneNumber,
    whatsAppChatMetaData?.thread?.id,
    whatsAppChatMetaData?.thread?.service_window_open,
    whatsAppChatMetaData?.thread?.service_window_closes_at,
    whatsappChats,
    whatsappChatScreenLoading,
    // F9 — re-merge when the open thread's call events land/refresh.
    threadCallEvents,
    threadCallEventsThreadId,
  ]);

  // After each render, mark every currently-rendered message id as seen
  // so the next render cycle only animates truly-new arrivals.
  React.useEffect(() => {
    (whatsappMessages || []).forEach((m) => {
      if (m?.id != null) seenMessageIdsRef.current.add(m.id);
    });
  }, [whatsappMessages]);

  // Tier 3 / F-33 — snapshot unread count on thread open.
  // We capture the BE-reported count exactly once per (thread × open),
  // then leave it frozen until the user navigates away and back. This
  // way the divider doesn't disappear the moment our auto-scroll
  // triggers a `mark_as_read` (which zeroes `unread_count` server
  // side). Risk gate: if the field is missing, count stays 0 and the
  // divider doesn't render — fail closed, no crash.
  React.useEffect(() => {
    const threadId = whatsAppChatMetaData?.thread?.id ?? null;
    const reported = whatsAppChatMetaData?.thread?.unread_count;
    if (!threadId) {
      unreadSnapshotForThreadRef.current = { threadId: null, count: 0 };
      setUnreadDividerCount(0);
      setIsUnreadDividerVisible(false);
      return;
    }
    if (unreadSnapshotForThreadRef.current.threadId !== threadId) {
      const safe = typeof reported === 'number' && reported > 0 ? reported : 0;
      unreadSnapshotForThreadRef.current = { threadId, count: safe };
      setUnreadDividerCount(safe);
      // Reset visibility — the IO observer will fire on first render.
      setIsUnreadDividerVisible(false);
    }
    // Snapshot ONLY when the thread identity changes. unread_count is
    // intentionally NOT a dep — fetchWhatsappChatAndOtherMetaData
    // dispatches mark_as_read on open which then refreshes the thread
    // with unread_count=0; if we depended on unread_count we'd re-run
    // and zero the snapshot, vanishing the divider on every re-open
    // of the same thread within the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppChatMetaData?.thread?.id]);

  // Tier 3 / F-33 — track when the divider scrolls into the viewport
  // so we can hide the floating jump button. IntersectionObserver is
  // cheap and avoids tying us to scroll events. The observer is
  // re-attached whenever the divider mounts (i.e. when the count
  // changes from 0 → N) OR when the DOM node mounts for the first
  // time after messages finish loading (unreadDividerMounted).
  //
  // F6 FIX: Previously, deps was [unreadDividerCount] only. The IO
  // effect ran when the count changed, but messages load async so the
  // UnreadDivider DOM node hadn't mounted yet (unreadDividerRef.current
  // was null) — early return, IO never established, pill never hid.
  // Adding unreadDividerMounted (flipped by the callback ref when the
  // node mounts) causes the effect to re-run after the node is in the
  // DOM, establishing the IO correctly.
  React.useEffect(() => {
    const node = unreadDividerRef.current;
    if (!node || unreadDividerCount <= 0) {
      return undefined;
    }
    const root = scrollRef.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsUnreadDividerVisible(entry.isIntersecting);
        });
      },
      { root, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadDividerCount, unreadDividerMounted]);

  // Track D / F10 — when ConversationSearchModal opens a thread on a
  // message-body hit it stashes `scrollToMessageId` on the chat
  // metadata. Wait for the message list to load (the message we want
  // must be in `whatsappMessages`), then scroll-and-flash it via the
  // shared useScrollToMessage hook. Once scrolled, clear the flag so
  // we don't repeat on every re-render. The 250ms delay gives MUI's
  // InfiniteScroll layout a chance to settle before we measure the
  // bubble's offset.
  const scrolledForMsgIdRef = React.useRef(null);
  React.useEffect(() => {
    const targetId = whatsAppChatMetaData?.scrollToMessageId;
    if (targetId == null) return undefined;
    if (scrolledForMsgIdRef.current === targetId) return undefined;
    if (!whatsappMessages?.length) return undefined;
    const present = whatsappMessages.some((m) => `${m?.id}` === `${targetId}`);
    if (!present) return undefined;
    const t = setTimeout(() => {
      scrollToMessage(targetId);
      scrolledForMsgIdRef.current = targetId;
      // Clear the scroll-to flag on the metadata so future
      // re-renders don't re-trigger; structuredClone preserves
      // the rest of the metadata shape.
      const cloned = whatsAppChatMetaData ? { ...whatsAppChatMetaData, scrollToMessageId: null } : whatsAppChatMetaData;
      dispatch(saveWhatsappChatMetaData(cloned));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppChatMetaData?.scrollToMessageId, whatsappMessages]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      // Column-reverse container: scrollTop === 0 means pinned to the
      // newest message. Native WA treats anything within ~200 px as
      // "still at the bottom" and auto-follows new arrivals.
      const nearBottom = el.scrollTop > -200;
      if (nearBottom) scrollToBottom();
      else {
        // New message arrived while user is scrolled up — bump the
        // unread counter so the FAB's badge reflects how many they've
        // missed while browsing history.
        setUnreadWhileScrolled((c) => c + 1);
      }
    }
    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      const nearBottom = el.scrollTop > -200;
      setIsUserNearBottom(nearBottom);
      // If user scrolls themselves back to the bottom, clear the unread
      // marker — the FAB hides and the next arrival re-starts the count.
      if (nearBottom) {
        setUnreadWhileScrolled(0);
      }
    };
    const timer = setTimeout(() => {
      const el = scrollRef.current;
      if (el) {
        el.addEventListener('scroll', handleScroll);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const el = scrollRef.current;
      if (el) el.removeEventListener('scroll', handleScroll);
    };
  }, [whatsappMessages]);

  const scrollToBottom = () => {
    setIsUserNearBottom(true);
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
      setUnreadWhileScrolled(0);
    }
  };

  const fetchMoreMessages = () => {
    dispatch(fetchMoreWhastappMessages());
  };

  const handleCloseChat = () => {
    dispatch(saveWhatsappChatMetaData({}));
    // In embedded mode, don't navigate - just clear the chat
    // Navigate to base whatsapp URL to keep URL in sync with closed chat
    if (practiceId && !embedded) {
      navigate(PATH_DASHBOARD.whatsapp.root(practiceId));
    }
  };

  const handleCancelCloseConversation = () => {
    setOpenCloseConversation(false);
  };

  const handleConfirmCloseConversation = (reason) => {
    // CloseChatPopup already completed the ContactRound.
    // This just closes the WhatsApp thread.
    dispatch(
      closeWhatsappConversationWithReason(
        whatsAppChatMetaData?.thread?.id,
        reason,
        whatsAppChatMetaData?.leadDetails?.id
      )
    );
    setOpenCloseConversation(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleQuickCloseClick = (e) => setQuickCloseAnchor(e.currentTarget);
  const handleQuickCloseClose = () => setQuickCloseAnchor(null);
  const handleQuickClose = () => {
    handleQuickCloseClose();
    // Always open the full CloseChatPopup so the ContactRound flow runs.
    // Quick-close without ContactRound would skip the audit trail.
    setOpenCloseConversation(true);
  };

  // --- Bubble action sheet ----------------------------------------------
  // Eligibility shared with the swipe-to-reply gesture below — system,
  // template, revoked or empty bubbles don't expose meaningful actions.
  const isBubbleActionEligible = (msg) => {
    if (!msg || (msg.type !== 'sent' && msg.type !== 'received')) return false;
    if (msg.isRevoked || msg.isSpecialTemplateMessage) return false;
    const hasText = Boolean(msg.content || msg?.mediaMessage?.whatsapp_media_caption);
    const hasMedia = Boolean(msg?.mediaMessage?.whatsapp_media_url);
    return hasText || hasMedia;
  };
  // Track D / F2 multi-select. The longPress hook owns the
  // selection state — we never duplicate it in component state. The
  // action sheet's "Select" button enters multi-select with the current
  // message; once in multi-select, every long-press / tap on a bubble
  // toggles membership instead of opening the sheet.
  const longPressRef = React.useRef(null);
  const openBubbleMenu = React.useCallback((msg, anchorEl) => {
    if (msg?.id == null) return;
    // Already in multi-select? Long-press / right-click toggles membership.
    const lp = longPressRef.current;
    if (lp && lp.isSelecting) {
      if (isBubbleActionEligible(msg)) lp.toggleSelect(msg.id);
      return;
    }
    if (!isBubbleActionEligible(msg) || !anchorEl) return;
    setBubbleMenu({ anchorEl, msg });
  }, []);
  const closeBubbleMenu = () => setBubbleMenu(null);

  // Track C / F6 — edit eligibility. Outbound + text-only + non-revoked +
  // within Meta's 15-minute hard window (we drop the option silently per
  // D8 — no countdown warning). Re-evaluated on every render so the menu
  // item disappears the moment the window expires. The 15-minute literal
  // is hard-coded by Meta; defining it as a module-level constant would
  // make the eslint exhaustive-deps lint happy but at the cost of pulling
  // a magic number out of view from the eligibility check.
  const isBubbleEditEligible = React.useCallback((msg) => {
    const EDIT_WINDOW_MS = 15 * 60 * 1000;
    if (!msg) return false;
    if (msg.type !== 'sent') return false;
    if (msg.isRevoked) return false;
    if (msg.mediaMessageType && msg.mediaMessageType !== 'text') return false;
    // Pre-Meta-ack optimistic bubbles have no wamid — BE rejects edit
    // on those, and our editor identity (C-FE-2) keys off wamid, so
    // surface the constraint up-front. Fail-closed.
    if (!msg.wamid) return false;
    // API serialises createdAt as DD-MM-YYYY HH:mm:ss.ffffffZ (UK
    // day-first). `new Date(...)` on that returns Invalid Date,
    // which previously hid Edit on every server-confirmed message
    // — the bug this fixup is closing. We can't reuse the module
    // parseDate helper here because parsedDateTimeForWhatsapp emits
    // "MM-DD-YYYY HH:mm:ss.ffffffZ", and `new Date()` of a non-ISO
    // string with Z is undefined behaviour across engines (some
    // engines drop the Z and treat it as local time, blowing the
    // 15-minute eligibility window in summer). Build a real ISO
    // string instead: YYYY-MM-DDTHH:mm:ss.ffffffZ. Optimistic
    // sends already emit ISO, so they fall through the regex and
    // hit the native parser unchanged.
    try {
      const raw = msg.createdAt;
      if (!raw) return false;
      const m = typeof raw.match === 'function' ? raw.match(/^(\d{2})-(\d{2})-(\d{4})/) : null;
      const iso = m ? `${m[3]}-${m[2]}-${m[1]}T${raw.slice(11)}` : raw;
      const created = new Date(iso);
      if (Number.isNaN(created.getTime())) return false;
      return Date.now() - created.getTime() < EDIT_WINDOW_MS;
    } catch (_err) {
      // Unparseable timestamp — fail-closed. Better to hide Edit
      // than to expose a 15-minute window we can't enforce.
      return false;
    }
  }, []);

  const handleEditBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    // isBubbleEditEligible already guards on wamid presence, so the
    // setter below can trust msg.wamid is truthy.
    if (!msg || !isBubbleEditEligible(msg)) return;
    setEditingBubble({
      wamid: msg.wamid,
      draft: msg.content || '',
    });
  };

  const cancelEdit = React.useCallback(() => {
    setEditingBubble(null);
  }, []);

  const submitEdit = React.useCallback(() => {
    if (!editingBubble) return;
    const trimmed = (editingBubble.draft || '').trim();
    if (!trimmed) {
      // Empty body would 400 server-side; keep the editor open so the
      // operator can decide whether to cancel or rewrite.
      return;
    }
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) {
      dispatch(updateError({ message: 'Thread not loaded — please retry.', success: false }));
      return;
    }
    // editingBubble keys off wamid (stable across optimistic→confirmed
    // swap). The slice's editMessage thunk takes the DB PK in the URL,
    // so resolve current msg.id by wamid lookup at dispatch time. If
    // the message has rotated out of view (paginated away) we abort —
    // can't edit what we can't optimistically swap.
    const { wamid, draft } = editingBubble;
    const target = (whatsappChats || []).find((m) => m?.wamid && m.wamid === wamid);
    if (!target?.id) {
      setEditingBubble(null);
      dispatch(updateError({ message: 'Message no longer in view — please retry.', success: false }));
      return;
    }
    // Optimistic Save → close the editor immediately. The slice's
    // editMessage thunk handles optimistic body swap + revert on failure;
    // we don't keep the textfield open while waiting because that
    // produces a flash of stale state on success.
    setEditingBubble(null);
    dispatch(editMessage(threadId, target.id, draft));
  }, [editingBubble, whatsAppChatMetaData?.thread?.id, whatsappChats, dispatch]);

  const handleInfoBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    if (!msg) return;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) return;
    setMessageInfoTarget({ messageId: msg.id, threadId });
  };

  const longPress = useMessageLongPress({ onActivate: openBubbleMenu });
  const { isSelecting, selectedIds, toggleSelect, clearSelect } = longPress;
  // Keep a ref so openBubbleMenu sees the latest isSelecting flag
  // without having to reconstruct the callback on every state change.
  React.useEffect(() => {
    longPressRef.current = longPress;
  }, [longPress]);
  const { scrollToMessage } = useScrollToMessage();

  // Track D / F3 — Forward picker dialog open state. Selection lives in
  // the longPress hook; this only tracks dialog visibility + the IDs we
  // captured at open time so the user can keep mutating selection
  // without it changing under the dialog.
  const [forwardPicker, setForwardPicker] = React.useState({ open: false, ids: [] });

  // Strip WhatsApp markup so clipboard gets plain text, not "*bold*" runs.
  const plainBubbleText = (msg) => {
    const raw = msg?.content || msg?.mediaMessage?.whatsapp_media_caption || '';
    if (!raw) return '';
    const html = parseWhatsappMarkup(raw);
    if (typeof html !== 'string') return raw;
    // Parse as DOM and extract textContent — safer than regex for nested tags.
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return (doc.body.textContent || raw).trim();
    } catch (err) {
      return raw;
    }
  };

  const handleCopyBubble = async () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    const text = plainBubbleText(msg);
    if (!text) {
      dispatch(updateError({ success: false, message: 'Nothing to copy' }));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      dispatch(updateError({ success: true, message: 'Copied to clipboard' }));
    } catch (err) {
      dispatch(updateError({ success: false, message: 'Copy failed' }));
    }
  };

  // `msg` is optional — falls back to the right-click menu's selected
  // message so the existing menu wiring keeps working unchanged. Mobile
  // swipe-to-reply passes the message directly without going via the menu.
  const handleReplyBubble = (msg) => {
    const target = msg || bubbleMenu?.msg;
    closeBubbleMenu();
    if (!target) return;
    // The optimistic "sending" placeholder doesn't have a wamid yet — we
    // can't quote it because Meta needs the real wamid in `context`. The
    // operator will need to wait for the bubble to confirm, then reply.
    const wamid = target?.wamid;
    if (!wamid) {
      dispatch(
        updateError({
          success: 'warning',
          message: "This message hasn't been delivered yet — try replying once it confirms.",
        })
      );
      return;
    }
    const text = plainBubbleText(target);
    const fromWaid =
      target.type === 'sent'
        ? whatsAppChatMetaData?.phoneNumber || ''
        : target?.fromWaid || whatsAppChatMetaData?.phoneNumber || '';
    setReplyingTo({
      wamid,
      body: text,
      fromWaid,
      isFromMe: target.type === 'sent',
    });
    // Pop the cursor into the textarea so the operator can type
    // straight away — matches native WA's reply UX.
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // --- Swipe-to-reply (mobile) ------------------------------------------
  // Reuses the same eligibility rule as the bubble action sheet so a
  // bubble that can't open the menu also can't be swiped to reply.
  const isBubbleReplyEligible = isBubbleActionEligible;

  // Threshold + visual-cap constants for the swipe gesture. Matched to
  // native WA on Android — feels responsive on phones, doesn't fight
  // vertical scroll. Both in CSS px.
  const SWIPE_THRESHOLD_PX = 40;
  const SWIPE_MAX_TRANSLATE_PX = 60;
  // Vertical-vs-horizontal disambiguation. We need a small movement
  // budget before deciding direction so a perfectly diagonal flick
  // doesn't immediately get classified as scroll.
  const SWIPE_DIRECTION_LOCK_PX = 6;

  const handleBubbleTouchStart = (event, msg) => {
    if (!isMobile) return;
    if (!isBubbleReplyEligible(msg)) return;
    // Multi-touch (pinch-zoom on a media bubble) — bail.
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    // currentTarget is the bubble Box itself (the element we attached
    // the touch handlers to). Its parentNode is `messageContent` —
    // that's what we translate so the bubble + any meta below it move
    // together. The indicator sits inside the wrapper (one level up
    // from messageContent) and is found via [data-swipe-indicator].
    const bubbleEl = event.currentTarget;
    const contentEl = bubbleEl?.parentNode;
    const wrapperEl = contentEl?.parentNode;
    if (!contentEl || !wrapperEl) return;
    const indicatorEl = wrapperEl.querySelector('[data-swipe-indicator="1"]') || null;
    swipeStateRef.current = {
      id: msg.id,
      startX: touch.clientX,
      startY: touch.clientY,
      deltaX: 0,
      contentEl,
      indicatorEl,
      msg,
      cancelled: false,
      locked: null, // 'horizontal' | 'vertical' | null
    };
  };

  const handleBubbleTouchMove = (event) => {
    const state = swipeStateRef.current;
    if (!state || state.cancelled) return;
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;

    // Direction lock: once we know whether this is a scroll or a swipe,
    // commit and don't second-guess. Vertical wins if |dy| > |dx| * 1.5.
    if (state.locked == null) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx < SWIPE_DIRECTION_LOCK_PX && absDy < SWIPE_DIRECTION_LOCK_PX) {
        return; // wait for a clearer signal
      }
      if (absDy > absDx * 1.5) {
        // User is scrolling — yield to the chat list.
        state.cancelled = true;
        return;
      }
      state.locked = 'horizontal';
    }

    // Only rightward swipes trigger reply (matches native WA — a
    // leftward swipe on Android is a separate "info" gesture we don't
    // implement). Snap-back happens on touchend regardless.
    const clamped = Math.max(0, Math.min(dx, SWIPE_MAX_TRANSLATE_PX));
    state.deltaX = clamped;

    // Mutate DOM directly — bypassing React for the drag frames keeps
    // the rest of the chat list out of the render path. We restore the
    // inline styles to defaults on touchend.
    if (state.contentEl) {
      state.contentEl.style.transition = 'none';
      state.contentEl.style.transform = `translateX(${clamped}px)`;
    }
    if (state.indicatorEl) {
      // Fade in proportionally; max opacity at threshold.
      const progress = Math.min(clamped / SWIPE_THRESHOLD_PX, 1);
      state.indicatorEl.style.opacity = String(progress);
      // Slight scale-up at threshold for the "armed" feel.
      const scale = 0.6 + progress * 0.4;
      state.indicatorEl.style.transform = `translateY(-50%) scale(${scale})`;
    }
  };

  const handleBubbleTouchEnd = () => {
    const state = swipeStateRef.current;
    swipeStateRef.current = null;
    if (!state) return;
    const triggered = !state.cancelled && state.deltaX >= SWIPE_THRESHOLD_PX;
    // Snap-back animation regardless — even on a successful swipe the
    // bubble returns home, with the threaded reply UI taking the focus.
    if (state.contentEl) {
      state.contentEl.style.transition = 'transform 180ms cubic-bezier(0.2, 0.9, 0.3, 1)';
      state.contentEl.style.transform = 'translateX(0)';
    }
    if (state.indicatorEl) {
      if (triggered) {
        // Brief flash on success — more saturated, slightly larger,
        // then fade with the snap-back so it doesn't linger.
        state.indicatorEl.style.transition = 'opacity 220ms ease, transform 220ms ease';
        state.indicatorEl.style.opacity = '1';
        state.indicatorEl.style.transform = 'translateY(-50%) scale(1.15)';
        const el = state.indicatorEl;
        setTimeout(() => {
          if (!el) return;
          el.style.opacity = '0';
          el.style.transform = 'translateY(-50%) scale(0.6)';
        }, 150);
      } else {
        state.indicatorEl.style.transition = 'opacity 180ms ease, transform 180ms ease';
        state.indicatorEl.style.opacity = '0';
        state.indicatorEl.style.transform = 'translateY(-50%) scale(0.6)';
      }
    }
    if (triggered) {
      handleReplyBubble(state.msg);
    }
  };

  const handleReactionPick = (emoji) => {
    const msg = bubbleMenu?.msg;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    closeBubbleMenu();
    dispatch(reactToMessage(threadId, msg.id, emoji));
  };

  const handleReactionRemove = () => {
    const msg = bubbleMenu?.msg;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    closeBubbleMenu();
    dispatch(reactToMessage(threadId, msg.id, ''));
  };

  const handleDeleteBubble = () => {
    const msg = bubbleMenu?.msg;
    if (!msg) return;
    closeBubbleMenu();
    setRevokeTarget(msg);
  };

  const handleConfirmRevoke = () => {
    const msg = revokeTarget;
    const threadId = whatsAppChatMetaData?.thread?.id;
    setRevokeTarget(null);
    if (!msg?.id || !threadId) return;
    dispatch(revokeMessage(threadId, msg.id));
  };

  // Track D / F3 — open the ForwardPickerDialog. Two callsites:
  //   1. From the action sheet (single-message forward)
  //   2. From MultiSelectTopBar.onForward (bulk forward)
  // Snapshot the ids at open time so the dialog isn't sensitive to
  // subsequent selection changes.
  const handleForwardBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    if (msg?.id == null) {
      dispatch(updateError({ success: false, message: 'Nothing to forward' }));
      return;
    }
    setForwardPicker({ open: true, ids: [msg.id] });
  };

  const handleBulkForward = () => {
    if (!selectedIds.length) return;
    setForwardPicker({ open: true, ids: [...selectedIds] });
  };

  const closeForwardPicker = () => setForwardPicker({ open: false, ids: [] });

  const confirmForward = (targetThreadId) => {
    const { ids } = forwardPicker;
    if (!ids?.length || !targetThreadId) {
      closeForwardPicker();
      return;
    }
    dispatch(
      forwardMessages({
        messageIds: ids,
        targetThreadId,
        onSuccess: () => {
          closeForwardPicker();
          clearSelect();
        },
      })
    );
  };

  // Track D / F2 — bulk Copy. Multi-line join with blank line so the
  // pasted block keeps message-level separation. Filters out media-
  // only messages without text/caption.
  const handleBulkCopy = async () => {
    if (!selectedIds.length) return;
    const lookup = new Map((whatsappMessages ?? []).map((m) => [m.id, m]));
    const texts = selectedIds
      .map((id) => lookup.get(id))
      .filter(Boolean)
      .map((m) => plainBubbleText(m))
      .filter(Boolean);
    if (!texts.length) {
      dispatch(updateError({ success: false, message: 'Nothing to copy' }));
      return;
    }
    try {
      await navigator.clipboard.writeText(texts.join('\n\n'));
      dispatch(
        updateError({
          success: true,
          message: `Copied ${texts.length} message${texts.length === 1 ? '' : 's'}`,
        })
      );
      clearSelect();
    } catch (err) {
      dispatch(updateError({ success: false, message: 'Copy failed' }));
    }
  };

  // Track D / F2 — bulk Delete. Outbound only. Inbound messages can't
  // be revoked on Meta's side, so we surface that to the operator
  // rather than silently skipping. Now wired through Track A's
  // revokeMessage thunk (W-2) — fan out via Promise.all and toast the
  // outcome.
  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) return;
    const lookup = new Map((whatsappMessages ?? []).map((m) => [m.id, m]));
    const selected = selectedIds.map((id) => lookup.get(id)).filter(Boolean);
    // Outbound = author = the connected phone number. The serializer
    // already normalises `type === 'sent'` to mean outbound for both
    // optimistic and server-confirmed messages. Skip already-revoked
    // to avoid 404s on Meta's side.
    const outbound = selected.filter((m) => m.type === 'sent' && !m.isRevoked);
    const inbound = selected.filter((m) => m.type !== 'sent');

    if (!outbound.length) {
      dispatch(
        updateError({
          success: 'warning',
          message: inbound.length ? 'Cannot delete received messages' : 'Selected messages are already deleted',
        })
      );
      return;
    }

    // Fan out the revokes; collect successes vs failures so we can
    // give the operator a single accurate toast at the end. We can't
    // know per-revoke success without re-reading state because the
    // thunk doesn't return ok/error — so we treat the dispatch chain
    // as best-effort and toast the optimistic counts. Bad responses
    // surface their own per-message error toasts via Track A's thunk.
    try {
      await Promise.all(outbound.map((m) => dispatch(revokeMessage(threadId, m.id))));
      const skipped = inbound.length;
      dispatch(
        updateError({
          success: true,
          message: skipped
            ? `Deleted ${outbound.length} message${outbound.length === 1 ? '' : 's'}, skipped ${skipped} received`
            : `Deleted ${outbound.length} message${outbound.length === 1 ? '' : 's'}`,
        })
      );
    } catch (err) {
      dispatch(
        updateError({
          success: false,
          message: 'Some messages failed to delete',
        })
      );
    } finally {
      clearSelect();
    }
  };

  // Track D / F2 + Track B — bulk Star (W-3). Fan out starMessage
  // thunks for every selected message regardless of inbound/outbound;
  // the BE accepts both per Track B's thunk semantics.
  const handleBulkStar = async () => {
    if (!selectedIds.length) return;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) return;
    try {
      await Promise.all(selectedIds.map((id) => dispatch(starMessage(threadId, id))));
      dispatch(
        updateError({
          success: true,
          message: `Starred ${selectedIds.length} message${selectedIds.length === 1 ? '' : 's'}`,
        })
      );
    } catch (err) {
      dispatch(
        updateError({
          success: false,
          message: 'Some messages failed to star',
        })
      );
    } finally {
      clearSelect();
    }
  };

  const handleStarBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    if (msg.isStarred) {
      dispatch(unstarMessage(threadId, msg.id));
    } else {
      dispatch(starMessage(threadId, msg.id));
    }
  };

  const handlePinBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    if (msg.isRevoked) {
      dispatch(updateError({ success: false, message: 'Cannot pin a deleted message' }));
      return;
    }
    // D5 — replace existing pin only after operator confirms.
    const existingPinId = whatsAppChatMetaData?.thread?.pinned_message?.id;
    if (existingPinId && String(existingPinId) !== String(msg.id)) {
      setReplacePinTarget({ id: msg.id, threadId });
      return;
    }
    dispatch(pinMessage(threadId, msg.id));
  };

  const confirmReplacePin = () => {
    if (replacePinTarget?.id && replacePinTarget?.threadId) {
      dispatch(pinMessage(replacePinTarget.threadId, replacePinTarget.id));
    }
    setReplacePinTarget(null);
  };

  // URL → thread backup-fetch.
  //
  // The parent <Whatsapp /> component handles direct deep links
  // (`/whatsapp/thread/:threadId`) by fetching the thread off the
  // current phone profile and seeding `whatsAppChatMetaData` before
  // ChatPage tries to render. That works for the happy path, but it
  // can leave the chat panel empty when:
  //   (a) the parent's effect hasn't fired yet (slow init / mounting
  //       race),
  //   (b) the parent's fetch failed silently and metadata stayed empty,
  //       or
  //   (c) ChatPage is reached via an unexpected mount path that bypasses
  //       the parent's URL effect entirely.
  //
  // As a defensive backup, when the URL carries a `threadId` but the
  // metadata is still empty (or pointing at a different thread), kick
  // the slice's standalone direct-load path. The thunk re-checks state
  // before fetching, so it's a no-op if the parent already won the
  // race. Keyed only on `urlThreadId` + WA-init readiness so we don't
  // refire on unrelated re-renders, and on `metaThreadId` so the effect
  // settles once metadata catches up.
  const metaThreadId = whatsAppChatMetaData?.thread?.id ?? null;
  const isWhatsappInitialized = !isLoading && currentBusinessId !== -1 && currentWhatsappPhoneId !== -1;
  React.useEffect(() => {
    if (!urlThreadId) return;
    if (!isWhatsappInitialized) return;
    if (whatsappChatScreenLoading) return;
    if (String(metaThreadId) === String(urlThreadId)) return;
    // Only fire when metadata is genuinely missing for this URL —
    // either truly empty or pointing at a stale/other thread. This
    // is the safety net for case (b)/(c); case (a) is handled by
    // the parent and resolves before this branch is reached.
    const metaIsEmptyOrStale =
      Object.keys(whatsAppChatMetaData ?? {}).length === 0 ||
      (metaThreadId && String(metaThreadId) !== String(urlThreadId));
    if (!metaIsEmptyOrStale) return;
    dispatch(fetchWhatsappChatAndOtherMetaData(urlThreadId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlThreadId, isWhatsappInitialized, metaThreadId, dispatch]);

  // Repair fetch — metadata exists but is missing its thread or lead
  // (partial seed from a sidebar click, a failed earlier fetch). Kept
  // in its own effect, keyed on the two booleans rather than on the
  // whole `whatsAppChatMetaData` object, so it cannot re-fire merely
  // because some unrelated field of the metadata was refreshed.
  const metaIsSeeded = Object.keys(whatsAppChatMetaData ?? {}).length > 0;
  const metaMissingThread = !whatsAppChatMetaData?.thread;
  const metaMissingLead = !whatsAppChatMetaData?.leadDetails;
  React.useEffect(() => {
    if (metaIsSeeded && (metaMissingThread || metaMissingLead)) {
      dispatch(fetchWhatsappChatAndOtherMetaData());
    }
  }, [dispatch, metaIsSeeded, metaMissingThread, metaMissingLead]);

  // Chat-switch reset. Everything below is "the operator opened a
  // DIFFERENT conversation": drop the staged attachment, the in-progress
  // inline edit, the open drawers, and ease the pane back to the latest
  // message.
  //
  // Keyed on `metaThreadId`, not on the whole `whatsAppChatMetaData`
  // object. It used to depend on the object itself, so any refresh that
  // handed Redux a new metadata object re-ran the whole block — and the
  // `scrollTo({ top: 0 })` below yanked the operator back to the bottom
  // of a conversation they were scrolled up in, and threw away a staged
  // attachment, while they had not switched chats at all. Live updates
  // for the open thread arrive through `whatsappChats`, which this
  // effect deliberately does not watch; the scroll handler further up
  // (keyed on `whatsappMessages`) is what follows new arrivals, and it
  // already leaves the scroll alone when the operator is reading back.
  React.useEffect(() => {
    setTemplateModalOpen(false);
    setCurrentIndex(0);
    // Clear any pending attachment when switching chats
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    attachmentPreviewRef.current = null;
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setAttachmentCaption('');
    // Track C / F6 — drop any in-progress inline edit / open Message
    // Info drawer when the operator switches threads. Stale wamid in
    // editingBubble is harmless (no msg.wamid match → editor doesn't
    // render) but visible affordance leaks across chats are confusing.
    setEditingBubble(null);
    setMessageInfoTarget(null);
    // Auto-scroll to bottom when entering a new chat. Native WA eases
    // into the latest message when it's already close — feels alive,
    // not teleported. If the latest is far away (fresh chat open, long
    // history), an instant jump is less jarring than a long animated
    // scroll. Column-reverse container → scrollTop === 0 is the bottom,
    // so distance-from-bottom is |scrollTop|.
    setTimeout(() => {
      const el = scrollRef.current;
      if (el) {
        const distanceFromBottom = Math.abs(el.scrollTop);
        const behavior = distanceFromBottom < el.clientHeight * 1.5 ? 'smooth' : 'auto';
        el.scrollTo({ top: 0, behavior });
        setIsUserNearBottom(true);
        setUnreadWhileScrolled(0);
      }
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaThreadId]);

  React.useEffect(() => {
    if (practiceId) dispatch(fetchAIWizardStatus(practiceId));
  }, [dispatch, practiceId]);

  React.useEffect(
    () => () => {
      // Cleanup: revoke any pending object URL on unmount
      if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
      dispatch(resetWhatsappChat());
    },
    [dispatch]
  );

  // Auto-cancel attachment when 24h window expires
  React.useEffect(() => {
    if (isWindowExpired && attachmentFile) handleCancelAttachment();
  }, [isWindowExpired]);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`;
    }
  }, [sendMessage]);

  // -----------------------------------------------------------
  // Tier 2 / F-20, F-21, F-22 — composer autocomplete helpers.
  //
  // The hook (composerAutocomplete) tracks which trigger ('/', ':',
  // '@') is active and the captured filter query. Each section
  // below derives its own `items` list from the current Redux /
  // emoji state, then a single `applyAutocompleteSelection` swaps
  // the in-progress token (e.g. ":smi" or "/welcome") for the
  // selected replacement (an emoji glyph, a template body, or a
  // {{merge_tag}} token).
  //
  // NB: these hooks must be declared before the early return below
  // (rules-of-hooks), even though they're only meaningful once the
  // chat metadata is loaded.
  // -----------------------------------------------------------
  const slashItems = React.useMemo(() => {
    if (composerAutocomplete.trigger !== '/') return [];
    const list = templatesForPicker;
    const q = (composerAutocomplete.query || '').toLowerCase();
    return list.filter((t) => !q || (t?.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [composerAutocomplete.trigger, composerAutocomplete.query, templatesForPicker]);

  const colonItems = React.useMemo(() => {
    if (composerAutocomplete.trigger !== ':') return [];
    return searchEmojiShortcodes(composerAutocomplete.query, 8);
  }, [composerAutocomplete.trigger, composerAutocomplete.query]);

  const atItems = React.useMemo(() => {
    if (composerAutocomplete.trigger !== '@') return [];
    const q = (composerAutocomplete.query || '').toLowerCase();
    return (availableMergeTags || []).filter((tag) => !q || tag.toLowerCase().includes(q)).slice(0, 8);
  }, [composerAutocomplete.trigger, composerAutocomplete.query, availableMergeTags]);

  const activeAutocompleteItems = React.useMemo(() => {
    if (composerAutocomplete.trigger === '/') return slashItems;
    if (composerAutocomplete.trigger === ':') return colonItems;
    if (composerAutocomplete.trigger === '@') return atItems;
    return [];
  }, [composerAutocomplete.trigger, slashItems, colonItems, atItems]);

  const renderAutocompleteLabel = React.useCallback(
    (item) => {
      if (composerAutocomplete.trigger === '/') return formatTemplateName(item?.name);
      if (composerAutocomplete.trigger === ':') return `${item?.emoji}  :${item?.name}:`;
      if (composerAutocomplete.trigger === '@') return `{{${item}}}`;
      return String(item);
    },
    [composerAutocomplete.trigger]
  );

  const renderAutocompleteDescription = React.useCallback(
    (item) => {
      if (composerAutocomplete.trigger === '/') return getTemplateBody(item)?.slice(0, 80) || '';
      return null;
    },
    [composerAutocomplete.trigger]
  );

  // Insert the picked replacement into the textarea, splicing out
  // the in-progress trigger token. Restores caret position to the
  // end of the inserted replacement so the operator can keep typing.
  const applyAutocompleteSelection = React.useCallback(
    (item) => {
      const textarea = inputRef.current;
      const { trigger } = composerAutocomplete;
      const { start, end } = composerAutocomplete.matchRange || {};
      if (!textarea || trigger == null) {
        composerAutocomplete.close();
        return;
      }
      let replacement = '';
      if (trigger === '/') {
        replacement = getTemplateBody(item) || '';
      } else if (trigger === ':') {
        replacement = item?.emoji || '';
      } else if (trigger === '@') {
        replacement = `{{${item}}}`;
      }

      const before = sendMessage.slice(0, start);
      const after = sendMessage.slice(end);
      const next = before + replacement + after;
      setSendMessage(next);
      persistDraft(next);
      composerAutocomplete.close();
      requestAnimationFrame(() => {
        const caretPos = (before + replacement).length;
        textarea.focus();
        try {
          textarea.selectionStart = caretPos;
          textarea.selectionEnd = caretPos;
        } catch (_) {
          /* DOM may not be ready */
        }
      });
    },
    [composerAutocomplete, sendMessage, persistDraft]
  );

  // Build the keydown handler bound to the active items list. The
  // textarea's onKeyDown calls this FIRST; if it returns true the
  // textarea's own Enter / Cmd+B / Cmd+I handlers are short-circuited.
  const handleAutocompleteKeyDown = React.useCallback(
    (evt) => {
      if (!composerAutocomplete.trigger) return false;
      const handler = composerAutocomplete.buildKeyDownHandler({
        itemCount: activeAutocompleteItems.length,
        onSelect: (idx) => {
          const item = activeAutocompleteItems[idx];
          if (item != null) applyAutocompleteSelection(item);
        },
      });
      return handler(evt);
    },
    [composerAutocomplete, activeAutocompleteItems, applyAutocompleteSelection]
  );

  // -----------------------------------------------------------
  // Tier 2 / F-16 — drag-and-drop attachment.
  //
  // Wired on the chat-panel root so the drop target is generous
  // (whole conversation area, not just the composer). Multi-file
  // drops are queued and processed sequentially through the same
  // single-file path the "+" attachment menu uses, so the operator
  // gets caption + cancel for each file in turn.
  //
  // Service-window guard: outside the window we surface a toast
  // and abort. Inside, we filter to image/video/PDF/doc MIME
  // types (anything else is silently dropped).
  // -----------------------------------------------------------
  const ALLOWED_DROP_MIME_PREFIXES = React.useMemo(() => ['image/jpeg', 'image/png', 'video/mp4', 'video/3gpp'], []);
  const ALLOWED_DROP_EXT = React.useMemo(() => ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt'], []);
  const isAttachmentEligibleFile = React.useCallback(
    (file) => {
      if (!file) return false;
      const type = (file.type || '').toLowerCase();
      if (ALLOWED_DROP_MIME_PREFIXES.some((p) => type === p || type.startsWith(p))) return true;
      const name = (file.name || '').toLowerCase();
      return ALLOWED_DROP_EXT.some((ext) => name.endsWith(ext));
    },
    [ALLOWED_DROP_MIME_PREFIXES, ALLOWED_DROP_EXT]
  );

  const beginAttachmentForFile = React.useCallback((file) => {
    if (!file) return;
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    attachmentPreviewRef.current = null;
    setAttachmentFile(file);
    setAttachmentCaption('');
    if (file.type?.startsWith('image/') || file.type?.startsWith('video/')) {
      const previewUrl = URL.createObjectURL(file);
      setAttachmentPreview(previewUrl);
      attachmentPreviewRef.current = previewUrl;
    } else {
      setAttachmentPreview(null);
    }
  }, []);

  const handleChatPanelDragEnter = React.useCallback((evt) => {
    const types = evt?.dataTransfer?.types;
    if (!types) return;
    const hasFiles = Array.isArray(types) ? types.includes('Files') : Array.from(types).includes('Files');
    if (!hasFiles) return;
    evt.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  }, []);
  const handleChatPanelDragOver = React.useCallback((evt) => {
    const types = evt?.dataTransfer?.types;
    if (!types) return;
    const hasFiles = Array.isArray(types) ? types.includes('Files') : Array.from(types).includes('Files');
    if (!hasFiles) return;
    evt.preventDefault();
    if (evt.dataTransfer) evt.dataTransfer.dropEffect = 'copy';
  }, []);
  const handleChatPanelDragLeave = React.useCallback((evt) => {
    if (!evt?.dataTransfer?.types) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  }, []);
  const handleChatPanelDrop = React.useCallback(
    (evt) => {
      evt.preventDefault();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      const fileList = evt.dataTransfer?.files;
      if (!fileList || fileList.length === 0) return;
      if (isWindowExpired) {
        dispatch(updateError({ success: 'warning', message: '24-hour window closed — only templates can be sent.' }));
        return;
      }
      if (!isSendAllowed) {
        dispatch(updateError({ success: 'warning', message: 'Lead is not available for communication.' }));
        return;
      }
      const eligible = Array.from(fileList).filter(isAttachmentEligibleFile);
      if (eligible.length === 0) {
        dispatch(updateError({ success: 'warning', message: 'Unsupported file type.' }));
        return;
      }
      const [first, ...rest] = eligible;
      beginAttachmentForFile(first);
      if (rest.length > 0) setPendingDropQueue((q) => [...q, ...rest]);
    },
    [isWindowExpired, isSendAllowed, isAttachmentEligibleFile, beginAttachmentForFile, dispatch]
  );

  // When the current attachment clears (sent or cancelled) and we
  // still have queued files, advance to the next one.
  React.useEffect(() => {
    if (attachmentFile) return;
    if (pendingDropQueue.length === 0) return;
    const [next, ...rest] = pendingDropQueue;
    setPendingDropQueue(rest);
    beginAttachmentForFile(next);
  }, [attachmentFile, pendingDropQueue, beginAttachmentForFile]);

  // -----------------------------------------------------------
  // Tier 2 / F-17 — paste-from-clipboard handler.
  // Returns true when the paste was consumed (an image attached);
  // false to let the existing text-paste path run.
  // -----------------------------------------------------------
  const handleComposerPaste = React.useCallback(
    (evt) => {
      const items = evt?.clipboardData?.items;
      if (!items || items.length === 0) return false;
      let imageFile = null;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item?.kind === 'file' && (item.type || '').startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            imageFile = file;
            break;
          }
        }
      }
      if (!imageFile) return false;
      evt.preventDefault();
      if (isWindowExpired) {
        dispatch(updateError({ success: 'warning', message: '24-hour window closed — only templates can be sent.' }));
        return true;
      }
      if (!isSendAllowed) {
        dispatch(updateError({ success: 'warning', message: 'Lead is not available for communication.' }));
        return true;
      }
      beginAttachmentForFile(imageFile);
      return true;
    },
    [isWindowExpired, isSendAllowed, beginAttachmentForFile, dispatch]
  );

  // Tier 3 / F-33 — these hooks must be declared before the early
  // return below (rules-of-hooks). Both are pure derivations off
  // already-declared state, so the early-return path simply skips
  // computing the divider; nothing in the placeholder pane references
  // them.
  //
  // Index of the OLDEST unread inbound message in `whatsappMessages`.
  // The list is column-reverse (newest first), so counting
  // `unreadDividerCount` inbound bubbles from index 0 lands on the
  // boundary message — render the divider after that index in JSX so
  // it sits visually ABOVE it. We only count `received` bubbles
  // (operator-side: from the patient). Returns -1 when the divider
  // should not render (count <=0, no messages, or fewer inbound
  // messages than the snapshot count — e.g. older history not yet
  // loaded).
  const unreadDividerIndex = React.useMemo(() => {
    if (!unreadDividerCount || unreadDividerCount <= 0) return -1;
    if (!whatsappMessages?.length) return -1;
    let inboundSeen = 0;
    for (let i = 0; i < whatsappMessages.length; i += 1) {
      const m = whatsappMessages[i];
      if (m?.type === 'received') {
        inboundSeen += 1;
        if (inboundSeen === unreadDividerCount) return i;
      }
    }
    return -1;
  }, [whatsappMessages, unreadDividerCount]);

  // Jump-button handler — smooth-scrolls the divider into view.
  const scrollDividerIntoView = React.useCallback(() => {
    const node = unreadDividerRef.current;
    if (!node) return;
    try {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {
      // Older browsers may throw on the options object — fall back
      // to the no-arg form so the click still scrolls.
      node.scrollIntoView();
    }
  }, []);

  if (Object.keys(whatsAppChatMetaData ?? {}).length === 0 || isLoading) {
    return <SearchLeadForWhatsapp />;
  }

  const handleSendMessageChange = (e) => {
    const next = e?.target?.value ?? '';
    setSendMessage(next);
    // F-18 — push every change into the draft hook (it debounces
    // its own localStorage writes).
    persistDraft(next);
    // F-20 / F-21 / F-22 — re-evaluate autocomplete trigger off
    // the new value + the textarea's caret. We read selectionStart
    // synchronously off the event target so the regex sees the
    // pre-React state.
    const caret = e?.target?.selectionStart ?? next.length;
    composerAutocomplete.evaluate(next, caret);
  };

  const handleEmojiSelect = (emoji) => {
    const textarea = inputRef.current;
    if (!textarea) {
      setSendMessage((prev) => prev + emoji);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = sendMessage.substring(0, start) + emoji + sendMessage.substring(end);
    setSendMessage(newValue);
    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + emoji.length;
      textarea.selectionStart = pos;
      textarea.selectionEnd = pos;
    });
  };

  const applyFormatting = (marker) => {
    const textarea = inputRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = sendMessage.substring(start, end);

    let newValue;
    let cursorPos;

    if (selectedText) {
      newValue = sendMessage.substring(0, start) + marker + selectedText + marker + sendMessage.substring(end);
      cursorPos = end + marker.length * 2;
    } else {
      newValue = sendMessage.substring(0, start) + marker + marker + sendMessage.substring(end);
      cursorPos = start + marker.length;
    }

    setSendMessage(newValue);
    requestAnimationFrame(() => {
      textarea.focus();
      if (selectedText) {
        textarea.selectionStart = start;
        textarea.selectionEnd = cursorPos;
      } else {
        textarea.selectionStart = cursorPos;
        textarea.selectionEnd = cursorPos;
      }
    });
  };

  const sendWhatsappMessageHandler = (message) => {
    if (!(message?.trim()?.length > 0)) return;
    // An AI draft in the box goes out through the AI's own send, so the turn is
    // marked resolved and the message is recorded as the AI's. Sending it the
    // ordinary way would have published the same words as a staff reply and
    // left a PENDING draft behind it — which then reappears in the composer.
    if (composerHoldsAiDraft && aiComposerRef.current?.send) {
      const text = message;
      setAiDraftSending(true);
      Promise.resolve(aiComposerRef.current.send(text))
        .then((ok) => {
          if (ok === false) return;
          setSendMessage('');
          if (inputRef.current) inputRef.current.value = '';
          setAiDraftLoaded(null);
          clearPersistedDraft();
          if (onMessageSent) onMessageSent();
          // The AI send resolves only on a real success, so this is the
          // confirmed-send signal for this path (2026-10-13).
          if (onMessageConfirmed) onMessageConfirmed();
        })
        .finally(() => setAiDraftSending(false));
      return;
    }
    // Show optimistic "sending" bubble but keep the message in the input
    // until we know the send succeeded. On failure, restore the input.
    const savedMessage = message;
    // Snapshot the active reply BEFORE we clear it. If the send fails we
    // restore both the text and the reply so the operator doesn't lose
    // the threading intent.
    const savedReply = replyingTo;
    const repliedToWamid = replyingTo?.wamid || null;
    setSendMessage('');
    if (inputRef.current) inputRef.current.value = '';
    setReplyingTo(null);
    // F-18 — drop the saved draft once we've optimistically taken
    // the message off the screen. If the send fails the onFailure
    // handler restores both the input and the draft.
    clearPersistedDraft();
    if (onMessageSent) onMessageSent();
    setWhatsappMessages((prv) => [
      {
        id: Date.now(),
        timestamp: 'Now',
        isMessageInteractive: false,
        content: message,
        type: 'sent',
        status: 'sending',
        createdAt: new Date().toISOString(),
        // Mirror the threaded structure on the optimistic bubble so the
        // operator sees the quote-card immediately, before the server
        // round-trip resolves and re-fetches the canonical message list.
        repliedTo: savedReply
          ? {
              id: null,
              wamid: savedReply.wamid,
              body: savedReply.body,
              messageType: 'text',
              fromWaid: savedReply.fromWaid,
              isFromMe: savedReply.isFromMe,
            }
          : null,
      },
      ...(prv ?? []),
    ]);
    dispatch(
      sendWhatsappMessage(message, 'text', null, {
        repliedToWamid,
        onSuccess: onMessageConfirmed,
        onFailure: () => {
          // Restore the message and the reply context so the user
          // doesn't lose their work.
          setSendMessage(savedMessage);
          if (inputRef.current) inputRef.current.value = savedMessage;
          setReplyingTo(savedReply);
          // F-18 — restore the persisted draft too so a refresh
          // won't lose the failed send.
          persistDraft(savedMessage);
          // Remove the optimistic "sending" bubble
          setWhatsappMessages((prv) => (prv ?? []).filter((m) => m.status !== 'sending'));
        },
      })
    );
  };

  const sendWhatsappMessageTemplate = (message) => {
    if (!(message?.name?.trim()?.length > 0)) return;
    setTemplateModalOpen(false);
    setCurrentIndex(0);
    setSendMessage('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    // onSuccess added 2026-10-13: template sends are the ONLY way to open a cold
    // lead (outside Meta's 24h window free text is blocked), so a first-contact
    // "24 hr WhatsApp" step is almost always sent from here. Without this the
    // send->task-complete anchor never fires for the case it was built for.
    dispatch(sendWhatsappMessage(message?.name ?? '', 'template', handleCloseChat, {
        onSuccess: onMessageConfirmed,
    }));
    // Remove banner messages (windowAlert and templateRequired) and add the sent template + info message
    setWhatsappMessages((prv) => {
      // Filter out the banner messages
      const filteredMessages = (prv ?? []).filter(
        (msg) => msg.type !== 'windowAlert' && msg.type !== 'templateRequired'
      );
      // Add the new template message and an info message
      return [
        {
          id: `System${Date.now()}templateSentInfo`,
          type: 'system',
          // Not "sent". This line is written before the request resolves, so it
          // was claiming delivery on messages Meta went on to refuse — an
          // operator saw "Template sent!" for a template the patient never
          // received, and the only contradiction was a small red icon further
          // down. The bubble's own status is what reports the outcome; this
          // line explains the window rule and nothing else.
          content: 'Sending template. Once the patient replies you can message '
            + 'freely for 24 hours.',
          freeze: true,
        },
        {
          id: Date.now(),
          timestamp: 'Now',
          isMessageInteractive: false,
          content: getTemplateBody(message),
          type: 'sent',
          status: 'sending',
          createdAt: new Date().toISOString(),
        },
        ...filteredMessages,
      ];
    });
  };


  /**
   * Called by handlePersonalReplySend (the closed-window native-input flow) when
   * the agent hits Send.
   * `templateName` — the WhatsApp template name (e.g. "quick_reply_more_tod")
   * `extraContext`  — { first_name, treatment_name, practice_name, answer }
   * `composerCallbacks` — { onSuccess } so the caller resets itself only once
   *   the send actually succeeds.
   *
   * The existing sendWhatsappMessage thunk merges extraContext on top of the
   * BE-fetched context so the `answer` slot is always populated correctly.
   *
   * Mirrors sendWhatsappMessageHandler: we insert an optimistic bubble now, but
   * pass an onFailure that rolls it back so a HANDLED non-200 (template paused /
   * not approved / Meta rejection / BE validation) never leaves a fake "sent"
   * message on screen. On success the thunk refetches the thread, replacing the
   * optimistic rows with the real ones.
   */
  const handleQuickReplySend = (templateName, extraContext, composerCallbacks = {}) => {
    if (!(templateName?.trim()?.length > 0)) return;
    // Body for the optimistic "sending" bubble. Personal Reply passes the typed
    // answer via extraContext.answer; mapped templates have no answer, so the
    // caller passes composerCallbacks.optimisticContent (the rendered draft body)
    // to avoid a blank in-flight bubble.
    const optimisticContent = pickOptimisticContent(composerCallbacks.optimisticContent, extraContext?.answer);
    // Capture the optimistic rows' ids so onFailure can remove exactly them.
    const infoRowId = `System${Date.now()}templateSentInfo`;
    const optimisticBubbleId = Date.now();
    // Collapse the window-expired banner and show the sent optimistic bubble
    setWhatsappMessages((prv) => {
      const filteredMessages = (prv ?? []).filter(
        (msg) => msg.type !== 'windowAlert' && msg.type !== 'templateRequired'
      );
      return [
        {
          id: infoRowId,
          type: 'system',
          // Not "sent". This line is written before the request resolves, so it
          // was claiming delivery on messages Meta went on to refuse — an
          // operator saw "Template sent!" for a template the patient never
          // received, and the only contradiction was a small red icon further
          // down. The bubble's own status is what reports the outcome; this
          // line explains the window rule and nothing else.
          content: 'Sending template. Once the patient replies you can message '
            + 'freely for 24 hours.',
          freeze: true,
        },
        {
          id: optimisticBubbleId,
          timestamp: 'Now',
          isMessageInteractive: false,
          content: optimisticContent,
          type: 'sent',
          status: 'sending',
          createdAt: new Date().toISOString(),
        },
        ...filteredMessages,
      ];
    });
    dispatch(
      sendWhatsappMessage(templateName, 'template', handleCloseChat, {
        extraContext,
        onSuccess: () => {
          // Let the composer clear its selected template + typed answer.
          composerCallbacks.onSuccess?.();
          // Compose, don't replace: this is a confirmed send like any other, and
          // the mapped-template path is how a checklist-driven WhatsApp step is
          // normally sent (FollowUpPanel resolves a WTM and pins the draft).
          onMessageConfirmed?.();
        },
        onFailure: () => {
          // Roll back the optimistic rows so a failed send leaves NO bubble.
          setWhatsappMessages((prv) =>
            (prv ?? []).filter((m) => m.id !== infoRowId && m.id !== optimisticBubbleId)
          );
          // Composer keeps its selected template + answer for retry (it only
          // resets on success), so nothing to restore here.
        },
      })
    );
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachmentFile(file);
    setAttachmentCaption('');
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const previewUrl = URL.createObjectURL(file);
      setAttachmentPreview(previewUrl);
      attachmentPreviewRef.current = previewUrl;
    } else {
      setAttachmentPreview(null);
      attachmentPreviewRef.current = null;
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  // Attachment "+" popover — opens a native-WA-style grid of coloured
  // shortcuts. Each shortcut retargets the same hidden <input type="file">
  // with a narrower `accept` attribute so the OS picker scopes to the right
  // file family before handing back to handleFileSelect.
  const closeAttachmentMenu = () => setAttachmentMenuAnchor(null);
  const closeToolbarMenu = () => setToolbarMenuAnchor(null);
  const openComposerTools = (e) => setComposerToolsAnchor(e.currentTarget);
  const closeComposerTools = () => setComposerToolsAnchor(null);
  const openAttachmentFromComposerTools = () => {
    const anchor = composerToolsAnchor;
    closeComposerTools();
    setAttachmentMenuAnchor(anchor);
  };
  const openAiFromComposerTools = () => {
    const anchor = composerToolsAnchor;
    closeComposerTools();
    setAiMenuAnchor(anchor);
    setAiMenuOpen(true);
  };
  const triggerFilePicker = (acceptOverride) => {
    const input = fileInputRef.current;
    if (!input) return;
    // Default = original accept list (kept in sync with the <input> below).
    const defaultAccept = 'image/jpeg,image/png,video/mp4,video/3gpp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
    input.setAttribute('accept', acceptOverride || defaultAccept);
    closeAttachmentMenu();
    // Defer click to next tick so the popover close transition doesn't
    // race the file picker opening.
    requestAnimationFrame(() => input.click());
  };

  const handleCancelAttachment = () => {
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    attachmentPreviewRef.current = null;
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setAttachmentCaption('');
  };

  const handleSendAttachment = () => {
    if (!attachmentFile || !isSendAllowed || isWindowExpired) return;
    dispatch(sendWhatsappMediaMessage(attachmentFile, attachmentCaption.trim() || null));
    // Add optimistic message
    const isImage = attachmentFile.type?.startsWith('image/');
    setWhatsappMessages((prv) => [
      {
        id: Date.now(),
        timestamp: 'Now',
        isMessageInteractive: false,
        content: attachmentCaption.trim() || (isImage ? '[Image]' : `[${attachmentFile.name}]`),
        type: 'sent',
        status: 'sending',
        createdAt: new Date().toISOString(),
      },
      ...(prv ?? []),
    ]);
    handleCancelAttachment();
  };

  const avatarColor = getAvatarColor(whatsAppChatMetaData?.name);

  // Group messages by date for date dividers
  // Returns the date to show ABOVE this message's group (rendered after message due to column-reverse)
  const getDateDividerKey = (index) => {
    const currentMsg = whatsappMessages[index];
    if (!currentMsg?.createdAt) return null;
    if (currentMsg.type === 'system' || currentMsg.type === 'windowAlert' || currentMsg.type === 'templateRequired')
      return null;

    const currentDate = getDateKey(currentMsg.createdAt);

    // For the oldest message in the list, always show its date
    if (index >= whatsappMessages.length - 1) {
      return formatDateForDivider(currentMsg.createdAt);
    }

    // Check the next message (which is older in time due to array order)
    const nextMsg = whatsappMessages[index + 1];
    // Skip system messages when checking
    if (nextMsg?.type === 'system' || nextMsg?.type === 'windowAlert' || nextMsg?.type === 'templateRequired') {
      // Find the next actual message
      let nextActualIndex = index + 2;
      while (nextActualIndex < whatsappMessages.length) {
        const msg = whatsappMessages[nextActualIndex];
        if (msg?.type !== 'system' && msg?.type !== 'windowAlert' && msg?.type !== 'templateRequired') {
          const nextDate = getDateKey(msg.createdAt);
          if (currentDate !== nextDate) {
            return formatDateForDivider(currentMsg.createdAt);
          }
          return null;
        }
        nextActualIndex += 1;
      }
      // No more actual messages, show this date
      return formatDateForDivider(currentMsg.createdAt);
    }

    if (!nextMsg?.createdAt) return formatDateForDivider(currentMsg.createdAt);

    const nextDate = getDateKey(nextMsg.createdAt);
    if (currentDate !== nextDate) {
      return formatDateForDivider(currentMsg.createdAt);
    }
    return null;
  };

  // AI Assistant Handlers
  const handleAiMenuClose = () => {
    setAiMenuOpen(false);
    setAiMenuAnchor(null);
  };

  const buildAiContext = () => {
    const phoneNumber = whatsAppChatMetaData?.phoneNumber;
    // API returns newest first (index 0 = latest message)
    // slice(0, 15) gets the 15 most recent messages
    // reverse() converts to chronological order (oldest first, newest last) for AI readability
    const recentMessages =
      whatsappChats
        ?.slice(0, 15)
        ?.map((msg) => ({
          role: arePhoneNumsEqual(msg.from_waid, phoneNumber) ? 'patient' : 'staff',
          content: msg.body || msg.content || '',
          timestamp: msg.created_at,
        }))
        .reverse() || [];

    return {
      leadName: whatsAppChatMetaData?.name,
      leadPhone: phoneNumber,
      treatment: whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.treatment?.name || '',
      treatmentStatus: whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.status || '',
      recentMessages,
      draftMessage: sendMessage,
    };
  };

  const handleAiSelectAction = (actionType, extraContext = {}) => {
    const context = { ...buildAiContext(), ...extraContext };
    setLastAiAction(actionType);
    setLastAiContext(context);
    dispatch(generateAiResponse(actionType, context));
  };

  const handleAiResultClose = () => {
    dispatch(clearAiAssistant());
    setLastAiAction(null);
    setLastAiContext(null);
  };

  const handleAiResultSend = (content) => {
    sendWhatsappMessageHandler(content);
    handleAiResultClose();
  };

  /** Rate one AI reply, and say so.
   *
   *  The thumbs saved silently when they saved at all — no toast, no spinner,
   *  nothing — so a click that fired no request (which, until the turn id moved
   *  onto the bubble, was every click in a practice that was not LIVE) looked
   *  exactly like a click that worked. The operator's only way to tell was to
   *  reload and see whether the thumb had stuck.
   */
  const handleRateMessage = async (payload) => {
    const rate = aiComposerRef.current?.rate;
    if (!rate) {
      dispatch(updateError({
        success: false,
        message: 'Rating is not available on this conversation right now.',
      }));
      return { ok: false };
    }
    const res = await rate(payload);
    if (res?.ok) {
      dispatch(updateError({
        success: true,
        message: res.cleared
          ? 'Rating removed.'
          : `Saved — marked ${payload?.verdict === 'GOOD' ? 'good' : 'bad'}.`,
      }));
    } else {
      dispatch(updateError({
        success: false,
        message: 'That rating did not save. Please try again.',
      }));
    }
    return res;
  };

  const handleAiRegenerate = () => {
    if (lastAiAction && lastAiContext) {
      dispatch(generateAiResponse(lastAiAction, lastAiContext));
    }
  };

  return (
    <Box
      sx={{
        ...styles.containerStyle,
        // Constrain to the visual viewport so the footer sits above the
        // keyboard on mobile. viewportH is null until the first visualViewport
        // event so we fall through to the default height:'100%' until then.
        ...(isMobile && viewportH != null ? { height: viewportH } : {}),
      }}
      onDragEnter={handleChatPanelDragEnter}
      onDragOver={handleChatPanelDragOver}
      onDragLeave={handleChatPanelDragLeave}
      onDrop={handleChatPanelDrop}
    >
      <GlobalStyles
        styles={{
          '.wa-highlight': {
            backgroundColor: '#FFFDE7',
            transition: 'background-color 0.6s ease-out',
          },
        }}
      />
      {/* Tier 2 / F-16 — drop overlay. Pointer-events:none so the
                overlay never intercepts the drop event itself; the root
                Box handler still receives it. */}
      {isDragOver && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 12,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 171, 85, 0.08)',
            border: `2px dashed ${COLORS.primary}`,
            borderRadius: '12px',
            margin: '8px',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.92)',
              padding: '12px 18px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
            }}
          >
            <Icon icon="mdi:tray-arrow-down" width={22} height={22} style={{ color: COLORS.primary }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>Drop to send</Typography>
          </Box>
        </Box>
      )}
      {/* Header */}
      <Box sx={styles.header}>
        {/* Contact Row */}
        <Box sx={styles.chatContactRow}>
          <Box sx={styles.avatarBlock}>
            {isMobile && !embedded && onBack && (
              <IconButton onClick={onBack} aria-label="Back to conversations" sx={{ minWidth: 44, minHeight: 44 }}>
                <Icon icon="mdi:arrow-left" width={20} />
              </IconButton>
            )}
            <Avatar
              {...stringAvatar(whatsAppChatMetaData?.avatar, whatsAppChatMetaData?.phoneNumber, {
                width: '36px',
                height: '36px',
                fontSize: '14px',
                backgroundColor: avatarColor,
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
              })}
              onClick={onToggleDetails}
              {...piiProps}
            />
            <Box sx={styles.userInfo}>
              <Box sx={styles.chatName}>
                <Typography component="span" {...piiProps} sx={{ fontSize: '16px', fontWeight: 600, color: COLORS.textPrimary }}>
                  {whatsAppChatMetaData?.name}
                </Typography>
                {canShowUnlinkedThreadActions && (
                  <Box
                    component="span"
                    sx={{ ...styles.statusBadge, backgroundColor: COLORS.errorBg, color: COLORS.errorDark }}
                  >
                    Not Registered
                  </Box>
                )}
                {whatsAppChatMetaData?.leadDetails?.archived_at && (
                  <Box
                    component="span"
                    sx={{ ...styles.statusBadge, backgroundColor: COLORS.errorBg, color: COLORS.errorDark }}
                  >
                    Archived
                  </Box>
                )}
              </Box>
              <Box sx={styles.chatSubtitle}>
                {whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.treatment && (
                  <Box component="span" sx={styles.chatTreatmentText}>
                    {whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.treatment}
                  </Box>
                )}
                {whatsAppChatMetaData?.thread?.id && (
                  <Tooltip title={serviceWindowTooltip} arrow>
                    <Box component="span" sx={styles.windowPill(serviceWindow.open)}>
                      <Box component="span" sx={styles.windowPillDot(serviceWindow.open)} />
                      {serviceWindowLabel}
                    </Box>
                  </Tooltip>
                )}
                {conversationClosed && (
                  <Box component="span" sx={{ ...styles.headerSignalPill, color: COLORS.errorDark }}>
                    <Icon icon="mdi:check-circle-outline" width={12} height={12} />
                    {closeReason}
                  </Box>
                )}
                {pinnedMessage?.id && (
                  <Box component="span" sx={styles.headerSignalPill}>
                    <Icon icon="mdi:pin" width={12} height={12} />
                    Pinned
                  </Box>
                )}
                {currentBusinessIsConnected !== true && (
                  <Box component="span" sx={styles.headerSignalPill}>
                    <Icon icon="mdi:history" width={12} height={12} />
                    Read-only history
                  </Box>
                )}
                {starredCount > 0 && (
                  <Box component="span" sx={styles.headerSignalPill}>
                    <Icon icon="mdi:star" width={12} height={12} />
                    {starredCount} starred
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, minHeight: 32 }}>
            {unreadCount > 0 && (
              <Box component="span" sx={styles.statusBadge}>
                {unreadCount} unread
              </Box>
            )}
            {/* WhatsApp AI agent — per-thread mode. Renders nothing when the
                agent is off for this practice, so nothing changes in the inbox
                for practices that have not enabled it. */}
            {whatsAppChatMetaData?.thread?.id && (
              <WhatsappAIModeControl
                practiceId={practiceId}
                threadId={whatsAppChatMetaData.thread.id}
                iconSx={styles.createLeadBtn}
                onDraftState={setAiComposer}
              />
            )}
            <WhatsappCallControls
              contact={{
                phoneNumber: whatsAppChatMetaData?.phoneNumber,
                name: whatsAppChatMetaData?.name,
                leadId: whatsAppChatMetaData?.leadDetails?.id || whatsAppChatMetaData?.thread?.lead?.id,
                threadId: whatsAppChatMetaData?.thread?.id,
              }}
              disabled={
                currentBusinessIsConnected !== true
                || whatsppSendLoading
                || whatsappChatScreenLoading
                || !whatsAppChatMetaData?.phoneNumber
              }
              iconSx={styles.createLeadBtn}
            />
            {!embedded && canShowUnlinkedThreadActions && (
              <Tooltip title="Create a CRM lead from this conversation" arrow>
                <Button
                  onClick={() => quickActionsRef.current?.createLead?.()}
                  disabled={whatsppSendLoading}
                  startIcon={<Icon icon="bi:person-add" width={17} height={17} />}
                  sx={styles.secondaryToolbarButton}
                >
                  Create lead
                </Button>
              </Tooltip>
            )}
            {conversationClosed ? (
              <Button
                onClick={() =>
                  dispatch(
                    reOpenWhatsappConversationWithReason(
                      whatsAppChatMetaData?.thread?.id,
                      whatsAppChatMetaData?.leadDetails?.id
                    )
                  )
                }
                disabled={whatsppSendLoading}
                startIcon={<Icon icon="mdi:restore" width={18} height={18} />}
                sx={styles.reopenBtn}
                aria-label="Reopen conversation"
              >
                Reopen
              </Button>
            ) : !hideResolve && (
              <Tooltip title="Finish this conversation" arrow>
                <Button
                  onClick={() => (onOpenResolve ? onOpenResolve() : setOpenCloseConversation(true))}
                  disabled={whatsppSendLoading}
                  startIcon={<Icon icon="mdi:check-circle-outline" width={18} height={18} />}
                  sx={styles.resolveBtn}
                  aria-label="Resolve conversation"
                >
                  Resolve
                </Button>
              </Tooltip>
            )}
            <Tooltip title="More actions" arrow>
              <span>
                <IconButton
                  onClick={(event) => {
                    if (hasLinkedLead || embedded) {
                      quickActionsRef.current?.openMenu?.(event);
                      return;
                    }
                    setToolbarMenuAnchor(event.currentTarget);
                  }}
                  sx={styles.createLeadBtn}
                  aria-label="More conversation actions"
                  disabled={!hasThreadLoaded && !embedded}
                >
                  <Icon icon="mdi:dots-horizontal" width={20} height={20} />
                </IconButton>
              </span>
            </Tooltip>
            {canShowUnlinkedThreadActions && !embedded && (
              <Popover
                open={Boolean(toolbarMenuAnchor)}
                anchorEl={toolbarMenuAnchor}
                onClose={closeToolbarMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: styles.toolbarMenuPaper } }}
              >
                <Box sx={{ py: 0.5 }}>
                  {whatsAppChatMetaData?.thread?.id && (
                    <MenuItem
                      onClick={() => {
                        closeToolbarMenu();
                        setStarredPanelOpen(true);
                      }}
                      sx={styles.toolbarMenuItem}
                    >
                      <Icon icon="mdi:star-outline" width={18} height={18} />
                      Starred messages
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      closeToolbarMenu();
                      dispatch(markAsUnRead(whatsAppChatMetaData?.thread?.id));
                    }}
                    disabled={whatsppSendLoading}
                    sx={styles.toolbarMenuItem}
                  >
                    <Icon icon="solar:chat-unread-outline" width={18} height={18} />
                    Mark as unread
                  </MenuItem>
                  {onToggleFocusMode && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <MenuItem
                        onClick={() => {
                          closeToolbarMenu();
                          onToggleFocusMode();
                        }}
                        sx={styles.toolbarMenuItem}
                      >
                        <Icon icon={isFocusMode ? 'mdi:arrow-collapse-all' : 'mdi:arrow-expand-all'} width={18} height={18} />
                        {isFocusMode ? 'Exit focus mode' : 'Focus mode'}
                      </MenuItem>
                    </>
                  )}
                  {onToggleFullscreen && (
                    <MenuItem
                      onClick={() => {
                        closeToolbarMenu();
                        onToggleFullscreen();
                      }}
                      sx={styles.toolbarMenuItem}
                    >
                      <Icon icon={isFullscreen ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'} width={18} height={18} />
                      {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    </MenuItem>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    onClick={() => {
                      closeToolbarMenu();
                      handleCloseChat();
                    }}
                    sx={{ ...styles.toolbarMenuItem, color: '#637381' }}
                  >
                    <Icon icon="mdi:close" width={18} height={18} />
                    Close chat
                  </MenuItem>
                </Box>
              </Popover>
            )}
          </Box>
        </Box>

        {/* Quick Close Popover */}
        <Popover
          open={Boolean(quickCloseAnchor)}
          anchorEl={quickCloseAnchor}
          onClose={handleQuickCloseClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 0.5 } } }}
        >
          <Box sx={{ py: 0.5 }}>
            <Typography
              sx={{
                px: 2,
                py: 1,
                fontSize: '12px',
                fontWeight: 600,
                color: COLORS.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Close Reason
            </Typography>
            {practiceChoices?.map((choice) => (
              <MenuItem
                key={choice}
                onClick={() => handleQuickClose(choice)}
                sx={{ fontSize: '13px', py: 0.75, px: 2 }}
              >
                {choice}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={() => {
                handleQuickCloseClose();
                setOpenCloseConversation(true);
              }}
              sx={{ fontSize: '13px', py: 0.75, px: 2, gap: 1, color: COLORS.textSecondary }}
            >
              <Icon icon="mdi:information-outline" width={16} height={16} />
              View lead details first...
            </MenuItem>
          </Box>
        </Popover>

        {/* Tag Info */}
        {whatsAppChatMetaData?.tags?.[0]?.name && !whatsappChatScreenLoading && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0.5 }}>
            <Tooltip title="Click to remove tag" placement="top" arrow>
              <Box
                component="span"
                onClick={() => dispatch(removeWhatsappTag(whatsAppChatMetaData?.thread?.id))}
                sx={{
                  ...styles.tagStyles,
                  background: whatsAppChatMetaData?.tags?.[0]?.colour ?? COLORS.warning,
                  color: COLORS.paper,
                  cursor: 'pointer',
                }}
              >
                {whatsAppChatMetaData?.tags?.[0]?.name}
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Quick Actions - Inside Header */}
        <QuickActions
          onToggleDetails={onToggleDetails}
          detailsOpen={detailsOpen}
          embedded={embedded}
          whatsppSendLoading={whatsppSendLoading}
          isSendAllowed={isSendAllowed}
          actionsRef={quickActionsRef}
          isMobile={isMobile}
          onOpenStarred={() => setStarredPanelOpen(true)}
          onToggleFocusMode={onToggleFocusMode}
          isFocusMode={isFocusMode}
          onToggleFullscreen={onToggleFullscreen}
          isFullscreen={isFullscreen}
          onCloseChat={handleCloseChat}
        />
      </Box>

      {/* Track B — Pinned message banner. Sits between the header
                and the messages stream. Renders nothing when no pin is
                set; the banner is sticky-by-position because it's fixed
                under the chat header in the document order. */}
      {whatsAppChatMetaData?.thread?.pinned_message?.id && (
        <PinnedMessageBanner
          pinnedMessage={whatsAppChatMetaData.thread.pinned_message}
          threadId={whatsAppChatMetaData.thread.id}
        />
      )}

      {/* ISS-025 — archived-lead re-engagement. Renders nothing unless the
                backend has flagged this conversation (thread.re_engaged_at),
                which only happens when an ARCHIVED patient has messaged again
                and no one has dealt with it yet. Sits directly above the
                message stream so the reason the composer is blocked is the
                first thing the rep reads. */}
      {whatsAppChatMetaData?.thread?.re_engaged_at && (
        <ReEngagementBanner
          reEngagedAt={whatsAppChatMetaData.thread.re_engaged_at}
          /* ISS-025B — `archived_at` is already on the wire: the thread's
             `lead` is serialised by BasicLeadWithTreatmentsSerializer, which
             inherits is_archived / archived_at from BasicLeadSerializer. No
             backend change was needed to show it. `leadDetails` is the
             fuller, separately-fetched payload, so prefer it when present. */
          archivedAt={
            whatsAppChatMetaData?.leadDetails?.archived_at
            || whatsAppChatMetaData?.thread?.lead?.archived_at
          }
          contactName={
            `${whatsAppChatMetaData?.thread?.lead?.first_name ?? ''} ${whatsAppChatMetaData?.thread?.lead?.last_name ?? ''}`.trim()
            || whatsAppChatMetaData?.thread?.name
            || ''
          }
          threadId={whatsAppChatMetaData.thread.id}
          leadId={whatsAppChatMetaData?.leadDetails?.id || whatsAppChatMetaData?.thread?.lead?.id}
          /* RCA 2026-08-28 — the banner must know whether the lead is actually
             archived, not just whether the thread carries a re-engagement
             marker (the two are independently stored and did diverge).

             Read off `thread.lead`, NOT `leadDetails`: the latter is rendered
             by LeadSerializer, whose Meta.fields does not include is_archived
             — and adding it there would expose it as a WRITABLE field on the
             lead PATCH endpoint, letting a client flip the archive flag
             without `lead.archive()` and its whole signal cascade. The thread's
             lead is serialised by BasicLeadWithTreatmentsSerializer, which
             inherits is_archived from BasicLeadSerializer, and is refetched
             whenever this conversation is opened.

             `??` (not `||`) so a real `false` survives; `null` for an
             unloaded payload means "unknown", which keeps the banner. */
          leadIsArchived={whatsAppChatMetaData?.thread?.lead?.is_archived ?? null}
          practiceId={practiceId}
          /* Clear the pane once hidden — the rep asked for this conversation
             to go away, so leaving it open and resolved contradicts them. */
          onIgnored={handleCloseChat}
        />
      )}

      {/* Track D / F2 — Multi-select top bar. Sticks below the
                header when any bubble is selected. */}
      <MultiSelectTopBar
        open={isSelecting}
        count={selectedIds.length}
        onCancel={clearSelect}
        onForward={handleBulkForward}
        onCopy={handleBulkCopy}
        onStar={handleBulkStar}
        onDelete={handleBulkDelete}
      />

      {/* Tier 3 / F-33 — floating "N unread ↓" jump button.
                Visible when:
                  - the thread had >0 unread when opened (snapshot), AND
                  - the divider is currently OFF-screen.
                Hides on viewport entry (IntersectionObserver) or count
                reset to 0. Click smooth-scrolls the divider into view —
                does not duplicate `mark_as_read` because the existing
                fetchWhatsappChatAndOtherMetaData side-effect already
                fired when the operator opened the thread. */}
      {unreadDividerCount > 0 && unreadDividerIndex >= 0 && !isUnreadDividerVisible && (
        <Box
          component="button"
          type="button"
          onClick={scrollDividerIntoView}
          sx={styles.unreadJumpBtn}
          aria-label={`${unreadDividerCount} unread message${unreadDividerCount === 1 ? '' : 's'}, jump to first`}
        >
          {`${unreadDividerCount} unread`}
          <Icon icon="mdi:arrow-down" width={14} height={14} />
        </Box>
      )}

      {/* Messages + Footer */}
      {
        <>
          {/* "Sending…" sweep bar — pinned to the top of the conversation panel
              (under the contact header), visible while any WhatsApp send is in
              flight; unmounts when whatsppSendLoading flips false. */}
          <WhatsappSendingBar sending={whatsppSendLoading} />
          {whatsappChatScreenLoading ? (
            <Box sx={styles.messagePane}>
              <WhatsappSkeleton />
            </Box>
          ) : (
            <Box
              id="chat-scrollable"
              ref={scrollRef}
              onClick={(e) => {
                // Track D / F2 — tap empty space exits multi-select.
                // Bubble clicks stop here only when the target is the
                // scrollable container itself (or its direct empty
                // children), not a bubble.
                if (!isSelecting) return;
                const { target } = e;
                if (!(target instanceof Element)) return;
                if (target.closest('[data-message-id]')) return;
                if (target.closest('[role="toolbar"]')) return;
                clearSelect();
              }}
              sx={{
                ...styles.messagePane,
                display: 'flex',
                flexDirection: 'column-reverse',
                position: 'relative',
              }}
            >
              {showMessageActionHint && !isSelecting && !hasNoMessages && (
                <Box sx={styles.messageActionHint}>
                  <Icon icon={isMobile ? 'mdi:gesture-tap-hold' : 'mdi:mouse-right-click-outline'} width={16} />
                  <Typography sx={styles.messageActionHintText}>
                    {isMobile ? 'Press and hold a message for actions.' : 'Right-click a message for actions.'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={dismissMessageActionHint}
                    sx={styles.messageActionHintClose}
                    aria-label="Dismiss message action hint"
                  >
                    <Icon icon="mdi:close" width={14} height={14} />
                  </IconButton>
                </Box>
              )}
              {hasNoMessages ? (
                <Box sx={styles.emptyConversationState}>
                  <Box sx={styles.emptyConversationIcon}>
                    <Icon icon="mdi:message-text-outline" width={24} height={24} />
                  </Box>
                  <Typography sx={styles.emptyConversationTitle}>No messages yet</Typography>
                  <Typography sx={styles.emptyConversationBody}>
                    Start with an approved template or create the lead before continuing the workflow.
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setTemplateModalOpen(true)}
                    startIcon={<Icon icon="mdi:file-document-outline" width={15} height={15} />}
                    sx={styles.emptyConversationButton}
                  >
                    Choose template
                  </Button>
                </Box>
              ) : (
                <InfiniteScroll
                  dataLength={whatsappMessages?.length ?? 0}
                  next={fetchMoreMessages}
                  hasMore={whatsAppChatNextAPI?.length > 0}
                  inverse
                  scrollableTarget="chat-scrollable"
                  loader={
                    whatsappChatNextAPILoading ? (
                      <Box sx={{ textAlign: 'center', padding: '16px' }}>
                        <Typography variant="caption" sx={{ color: COLORS.textDisabled }}>
                          Loading...
                        </Typography>
                      </Box>
                    ) : null
                  }
                  endMessage={
                    <Box sx={styles.messagesStart}>
                      <Typography variant="caption">Beginning of conversation</Typography>
                    </Box>
                  }
                  style={{ display: 'flex', flexDirection: 'column-reverse', overflowX: 'hidden' }}
                >
                <Box sx={{ display: 'flex', flexDirection: 'column-reverse', overflowX: 'hidden' }}>
                  {/* Live Personal Reply draft bubble — FIRST child of this
                      column-reverse list so it sits at the VISUAL BOTTOM, after
                      the newest real message, on the chat background. Shown
                      whenever Personal Reply is active (guiding the operator with
                      a placeholder line before they type). Hidden the instant a
                      send starts (!whatsppSendLoading) so the optimistic sent
                      bubble takes over with no duplicate; and it's gone after send
                      because onSuccess collapses PR (personalReplyActive → false). */}
                  <QuickReplyDraftBubble
                    preview={
                      isWindowExpired && personalReplyActive && !whatsppSendLoading
                        ? personalReply.preview
                        : null
                    }
                  />
                  {/* Mapped-template draft — the fully-merged approved-template body
                      preloaded from a checklist WhatsApp item. Shown regardless of
                      window state; hidden the instant a send starts so the
                      optimistic sent bubble takes over. */}
                  <QuickReplyDraftBubble
                    preview={mappedTemplateDraft && !whatsppSendLoading ? mappedTemplateDraft.preview : null}
                  />
                  {whatsappMessages?.map((msg, index) => {
                    // Window Alert — removed from chat flow (shown only in footer action bar)
                    if (msg.type === 'windowAlert') {
                      return null;
                    }

                    // Template Required Notice — removed (footer already shows this info)
                    if (msg.type === 'templateRequired') {
                      return null;
                    }

                    // Compute same-sender run boundaries. The list is rendered
                    // column-reverse, so index+1 is the visually ABOVE neighbor
                    // (older) and index-1 is visually BELOW (newer).
                    //   - isFirstInGroup (tail-bearing) = topmost in a run
                    //     → neighbor ABOVE is a different type.
                    //   - isLastInGroup = bottommost in a run
                    //     → neighbor BELOW is a different type.
                    const isBubbleMsg = msg.type === 'sent' || msg.type === 'received';
                    let isFirstInGroup = false;
                    let isLastInGroup = false;
                    if (isBubbleMsg) {
                      const prevAbove = whatsappMessages[index + 1];
                      const prevBelow = whatsappMessages[index - 1];
                      isFirstInGroup = !prevAbove || prevAbove.type !== msg.type;
                      isLastInGroup = !prevBelow || prevBelow.type !== msg.type;
                    }

                    // F9 — call events inline in the timeline. Rendered as a
                    // centered system-style pill (inherits styles.system) with
                    // a direction icon, label and the call's timestamp.
                    if (msg.type === 'callEvent') {
                      const callMeta = describeCallEvent(msg);
                      return (
                        <Box key={msg.id} sx={{ ...styles.system, display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                          <Icon icon={callMeta.icon} width={16} height={16} color={callMeta.color} />
                          <Typography sx={{ fontSize: '13px', color: callMeta.color, fontWeight: msg.isMissed ? 600 : 400 }}>
                            {callMeta.label}
                          </Typography>
                          {msg.timestamp ? (
                            <Typography sx={{ fontSize: '11px', color: '#8696A0' }}>
                              {msg.timestamp}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    }

                    // System Messages
                    if (msg.type === 'system') {
                      // addMediaButtons: render as inline button (no system pill wrapper)
                      if (msg?.specialType === 'addMediaButtons' && whatsAppChatMetaData?.leadDetails?.id) {
                        return (
                          <Box key={msg.id} sx={{ margin: '10px 0', textAlign: 'left', pl: '12px' }}>
                            <AddMediaButtons thread={msg} leadDetails={whatsAppChatMetaData?.leadDetails} />
                          </Box>
                        );
                      }
                      return (
                        <Box key={msg.id} sx={styles.system}>
                          <Typography sx={{ fontSize: '13px' }}>{msg.content}</Typography>
                        </Box>
                      );
                    }

                    // Date Divider (show after this message if date changes)
                    const dateDivider = getDateDividerKey(index);

                    // Determine bubble style — template messages get lavender, regular sent get green
                    // A message the AI wrote and sent itself. Not a template —
                    // those are the practice's own wording, chosen by a person
                    // in advance; this one nobody read before the patient did.
                    const isAiSent = !!msg.sentByAi && msg.type === 'sent' && !msg.isSpecialTemplateMessage;
                    // The test harness, not the agent. A live-test conversation
                    // resets itself after a handoff and says so in the thread,
                    // so those lines sit among real replies and read exactly
                    // like them. Matched on the prefix the backend writes
                    // (tasks.TEST_MODE_PREFIX) — it is the only thing in the
                    // product that produces it, and it is the one bubble a
                    // tester must not mistake for something a patient would
                    // ever receive.
                    const isTestMode = isAiSent
                      && typeof msg.content === 'string'
                      && msg.content.startsWith(TEST_MODE_PREFIX);
                    let bubbleTypeStyle = styles.messageBubbleReceived;
                    if (msg.type === 'sent') {
                      if (msg.isSpecialTemplateMessage) bubbleTypeStyle = styles.messageBubbleTemplate;
                      else if (isTestMode) bubbleTypeStyle = styles.messageBubbleTestMode;
                      else if (isAiSent) bubbleTypeStyle = styles.messageBubbleAi;
                      else bubbleTypeStyle = styles.messageBubbleSent;
                    }
                    // Only the first bubble in a same-sender run gets the CSS tail.
                    let bubbleTailStyle = null;
                    if (isFirstInGroup) {
                      if (msg.type !== 'sent') bubbleTailStyle = styles.messageBubbleFirstReceived;
                      else bubbleTailStyle = isAiSent ? styles.messageBubbleFirstAi : styles.messageBubbleFirstSent;
                    }

                    // Arrival animation — only for newly-arrived RECEIVED messages
                    // (not during initial thread load, not for the user's own sends
                    // which already appear instantly via the optimistic bubble).
                    const isNewArrival =
                      msg.type === 'received' &&
                      msg.id != null &&
                      !seenMessageIdsRef.current.has(msg.id) &&
                      seededForThreadRef.current != null;

                    // Optimistic-send pop — only the freshly-inserted "sending"
                    // bubble. Keyed on status 'sending' so it fires once and does
                    // NOT re-animate when it reconciles to the real message.
                    const isNewSent =
                      msg.type === 'sent' &&
                      msg.status === 'sending' &&
                      msg.id != null &&
                      !seenMessageIdsRef.current.has(msg.id) &&
                      seededForThreadRef.current != null;

                    // Collapsed template message — compact pill, expandable on click
                    const isTemplateExpanded = expandedTemplates.has(msg.id);
                    if (msg.isSpecialTemplateMessage && !isTemplateExpanded) {
                      return (
                        <React.Fragment key={msg.id}>
                          <Box
                            sx={{
                              ...styles.messageWrapper,
                              ...styles.messageWrapperSent,
                              ...(isLastInGroup ? styles.messageWrapperGroupEnd : {}),
                            }}
                          >
                            <Box sx={styles.messageContent}>
                              <Box
                                onClick={() => setExpandedTemplates((prev) => new Set([...prev, msg.id]))}
                                sx={styles.templateCollapsed}
                              >
                                <Tooltip
                                  title="Automated template sent to patient by the system — click to expand"
                                  arrow
                                  placement="left"
                                >
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                                  >
                                    <Icon
                                      icon="mdi:robot-outline"
                                      width={14}
                                      height={14}
                                      style={{ color: COLORS.primary }}
                                    />
                                  </Box>
                                </Tooltip>
                                <Typography component="span" sx={{ fontSize: '11px', color: COLORS.textDisabled }}>
                                  {msg.timestamp}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          {/* Tier 3 / F-33 — first-unread divider (template path).
                                                Rendered between the message and the date divider in
                                                JSX; column-reverse places it visually above the
                                                boundary message and below the date label. */}
                          {index === unreadDividerIndex && unreadDividerCount > 0 && (
                            <UnreadDivider ref={unreadDividerCallbackRef} count={unreadDividerCount} />
                          )}
                          {dateDivider && (
                            <Box sx={styles.dateDivider}>
                              <Typography sx={styles.dateDividerText}>{dateDivider}</Typography>
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    }

                    // Mobile-only swipe-to-reply: only attach touch handlers
                    // when on mobile AND the bubble is a reply-eligible target
                    // (mirrors the right-click menu's eligibility rules).
                    const swipeEnabled = isMobile && isBubbleReplyEligible(msg);

                    return (
                      <React.Fragment key={msg.id}>
                        {/* Message */}
                        <Box
                          id={msg.id != null ? `wa-msg-${msg.id}` : undefined}
                          data-message-id={msg.id != null ? msg.id : undefined}
                          sx={{
                            ...styles.messageWrapper,
                            ...(msg.type === 'sent' ? styles.messageWrapperSent : styles.messageWrapperReceived),
                            ...(isLastInGroup ? styles.messageWrapperGroupEnd : {}),
                            ...(isNewArrival ? styles.messageArriveReceived : {}),
                            ...(isNewSent ? styles.messageArriveSent : {}),
                            // Anchor the swipe-reply indicator absolutely against
                            // the wrapper so it stays put while the bubble slides.
                            ...(swipeEnabled ? { position: 'relative', overflow: 'visible' } : {}),
                          }}
                        >
                          {/* Swipe-to-reply indicator — fades in as the bubble is
                                                dragged right, full opacity at threshold. Mobile only.
                                                Sits on the LEFT of the wrapper regardless of bubble
                                                alignment, matching native WA. */}
                          {swipeEnabled && (
                            <Box
                              data-swipe-indicator="1"
                              sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%) scale(0.6)',
                                opacity: 0,
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: COLORS.primaryLighter,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                zIndex: 0,
                                willChange: 'opacity, transform',
                              }}
                            >
                              <Icon icon="mdi:reply" width={16} height={16} style={{ color: COLORS.primary }} />
                            </Box>
                          )}
                          {/* Avatar removed — native WhatsApp doesn't show avatars in 1:1 chats */}
                          <Box
                            sx={{
                              ...styles.messageContent,
                              ...(swipeEnabled ? { willChange: 'transform', position: 'relative', zIndex: 1 } : {}),
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            {/* Track D / F2 — selection checkbox. Renders only when in
                                                    multi-select mode. The actual toggle happens via the bubble
                                                    onClick (see lp.onClick override above) — we don't bind a
                                                    click handler here to avoid double-toggle. */}
                            {isSelecting && isBubbleActionEligible(msg) && msg.id != null && (
                              <Box
                                aria-label={selectedIds.includes(msg.id) ? 'Deselect message' : 'Select message'}
                                sx={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  border: `2px solid ${selectedIds.includes(msg.id) ? COLORS.primary : COLORS.divider}`,
                                  backgroundColor: selectedIds.includes(msg.id) ? COLORS.primary : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  transition: 'all 120ms ease',
                                }}
                              >
                                {selectedIds.includes(msg.id) ? (
                                  <Icon icon="mdi:check" width={14} height={14} style={{ color: '#FFFFFF' }} />
                                ) : null}
                              </Box>
                            )}
                            <Box
                              sx={{
                                ...styles.messageBubble,
                                ...bubbleTypeStyle,
                                ...(bubbleTailStyle || {}),
                                ...(msg.isSpecialTemplateMessage && isTemplateExpanded ? { cursor: 'pointer' } : {}),
                                ...(selectedIds.includes(msg.id)
                                  ? {
                                      outline: `2px solid ${COLORS.primary}`,
                                      outlineOffset: '2px',
                                    }
                                  : {}),
                              }}
                              {...(() => {
                                const lp = longPress.bind(msg);
                                const handlers = { onContextMenu: lp.onContextMenu };
                                if (swipeEnabled) {
                                  handlers.onTouchStart = (e) => {
                                    lp.onTouchStart(e);
                                    handleBubbleTouchStart(e, msg);
                                  };
                                  handlers.onTouchMove = (e) => {
                                    lp.onTouchMove(e);
                                    handleBubbleTouchMove(e);
                                    // Once swipe direction lock resolves to horizontal,
                                    // cancel the long-press timer to prevent the action
                                    // sheet from opening mid-swipe.
                                    if (swipeStateRef.current?.locked === 'horizontal') {
                                      longPress.cancelTimer();
                                    }
                                  };
                                  handlers.onTouchEnd = (e) => {
                                    lp.onTouchEnd(e);
                                    handleBubbleTouchEnd(e);
                                  };
                                  handlers.onTouchCancel = (e) => {
                                    lp.onTouchCancel(e);
                                    handleBubbleTouchEnd(e);
                                  };
                                } else {
                                  handlers.onTouchStart = lp.onTouchStart;
                                  handlers.onTouchMove = lp.onTouchMove;
                                  handlers.onTouchEnd = lp.onTouchEnd;
                                  handlers.onTouchCancel = lp.onTouchCancel;
                                }
                                // Track D / F2 — when in multi-select mode, every tap on
                                // a bubble toggles its membership. We still let lp.onClick
                                // run first so shift+click on desktop keeps working.
                                if (isSelecting) {
                                  handlers.onClick = (e) => {
                                    lp.onClick(e);
                                    if (e?.defaultPrevented) return;
                                    if (!isBubbleActionEligible(msg) || msg.id == null) return;
                                    toggleSelect(msg.id);
                                  };
                                } else if (msg.isSpecialTemplateMessage && isTemplateExpanded) {
                                  handlers.onClick = (e) => {
                                    lp.onClick(e);
                                    setExpandedTemplates((prev) => {
                                      const next = new Set(prev);
                                      next.delete(msg.id);
                                      return next;
                                    });
                                  };
                                } else {
                                  handlers.onClick = lp.onClick;
                                }
                                return handlers;
                              })()}
                            >
                              {msg.isRevoked && (
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontStyle: 'italic',
                                    color: 'rgba(0,0,0,0.45)',
                                    userSelect: 'none',
                                  }}
                                >
                                  This message was deleted
                                </Typography>
                              )}
                              {!msg.isRevoked && msg?.repliedTo && (
                                <Box
                                  onClick={() => scrollToMessage(msg.repliedTo.id)}
                                  sx={{
                                    display: 'flex',
                                    gap: '6px',
                                    alignItems: 'stretch',
                                    marginBottom: '6px',
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    borderLeft: `3px solid ${COLORS.primary}`,
                                    cursor: msg.repliedTo.id != null ? 'pointer' : 'default',
                                    maxWidth: '100%',
                                  }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      sx={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: COLORS.primary,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {msg.repliedTo.isFromMe
                                        ? 'You'
                                        : whatsAppChatMetaData?.name || whatsAppChatMetaData?.phoneNumber || 'Contact'}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: '13px',
                                        color: 'rgba(0, 0, 0, 0.6)',
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {msg.repliedTo.body ||
                                        (msg.repliedTo.messageType !== 'text' ? `[${msg.repliedTo.messageType}]` : '')}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              {/* Track D / W-1 — "↪ Forwarded" badge. Sits above
                                                        the body; keeps the layout in sync with native
                                                        WA so operators recognise it instantly. */}
                              {!msg.isRevoked && msg?.forwardedFrom && (
                                <Typography
                                  sx={{
                                    fontSize: '12px',
                                    fontStyle: 'italic',
                                    color: 'rgba(0,0,0,0.45)',
                                    marginBottom: '4px',
                                    userSelect: 'none',
                                  }}
                                >
                                  ↪ Forwarded
                                </Typography>
                              )}
                              {!msg.isRevoked && msg?.mediaMessage && (
                                <MediaDisplayComponent
                                  scrollToBottom={() => {}}
                                  whatsAppMessageMedia={msg?.mediaMessage ?? {}}
                                  messageType={msg?.mediaMessageType}
                                />
                              )}
                              {!msg.isRevoked &&
                                (msg.content || msg?.mediaMessage?.whatsapp_media_caption) &&
                                editingBubble?.wamid &&
                                msg.wamid &&
                                editingBubble.wamid === msg.wamid && (
                                  // Track C / F6 inline editor — bubble body becomes a
                                  // multiline TextField with Save / Cancel. Cmd/Ctrl+Enter
                                  // submits, Esc cancels. Saving stays in slice (optimistic
                                  // body swap + revert) — we close the editor immediately so
                                  // the operator sees the updated bubble without a flash.
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField
                                      autoFocus
                                      multiline
                                      minRows={1}
                                      maxRows={6}
                                      fullWidth
                                      size="small"
                                      value={editingBubble.draft}
                                      onChange={(e) =>
                                        setEditingBubble((prev) => (prev ? { ...prev, draft: e.target.value } : prev))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          e.preventDefault();
                                          cancelEdit();
                                        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                          e.preventDefault();
                                          submitEdit();
                                        }
                                      }}
                                      aria-label="Edit message"
                                      inputProps={{ 'aria-label': 'Edit message body' }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: 'rgba(255,255,255,0.6)',
                                          fontSize: '14px',
                                          lineHeight: 1.4,
                                        },
                                      }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                      <Button
                                        size="small"
                                        onClick={cancelEdit}
                                        sx={{ textTransform: 'none', fontSize: '12px' }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={submitEdit}
                                        disabled={!(editingBubble.draft || '').trim()}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: '12px',
                                          backgroundColor: COLORS.primary,
                                          '&:hover': { backgroundColor: COLORS.primaryDark },
                                        }}
                                      >
                                        Save
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              {/* Said once per run, above the words, in the
                                  bubble itself. A patient-facing reply nobody
                                  read before it went is the single thing in
                                  this thread an operator most needs to spot. */}
                              {isAiSent && isFirstInGroup && !msg.isRevoked && (
                                <Tooltip
                                  title={msg.aiTemplate
                                    ? 'The AI chose and sent an approved template. The wording is '
                                      + "the practice's, reviewed by Meta — not the model's."
                                    : 'The AI composed these words itself.'}
                                  placement="top"
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      ...(isTestMode
                                        ? styles.messageTestModeLabel
                                        : styles.messageAiLabel),
                                      display: 'flex',
                                    }}
                                  >
                                    <Icon
                                      icon={isTestMode
                                        ? 'solar:test-tube-bold-duotone'
                                        : 'solar:magic-stick-3-bold-duotone'}
                                      width={12}
                                      height={12}
                                    />
                                    {/* "AI Bot" on a template claimed the model
                                        wrote a sentence the practice wrote and
                                        Meta approved — the exact confusion this
                                        badge exists to prevent. The AI's part
                                        was choosing it and sending it, which is
                                        worth saying, and is a different claim. */}
                                    {(() => {
                                      if (isTestMode) return 'Test harness';
                                      return msg.aiTemplate ? 'AI sent template' : 'AI Bot';
                                    })()}
                                  </Box>
                                </Tooltip>
                              )}
                              {!msg.isRevoked &&
                                (msg.content || msg?.mediaMessage?.whatsapp_media_caption) &&
                                !(editingBubble?.wamid && msg.wamid && editingBubble.wamid === msg.wamid) && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '14px',
                                      lineHeight: 1.5,
                                      // A Stripe checkout URL is one ~100
                                      // character word with no space to break
                                      // on, so it overflowed the bubble and
                                      // the end of the link was unreadable --
                                      // on the one message a patient has to
                                      // act on. 'anywhere' breaks only when a
                                      // word cannot fit, so ordinary text
                                      // still wraps on spaces.
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: parseWhatsappMarkup(
                                          msg.content || msg?.mediaMessage?.whatsapp_media_caption
                                        ),
                                      }}
                                    />
                                  </Typography>
                                )}
                              {msg.isSpecialTemplateMessage && (
                                <Tooltip title="Automated template sent to patient by the system" arrow>
                                  <Box sx={styles.templateIndicator}>
                                    <Icon
                                      icon="mdi:robot-outline"
                                      width={14}
                                      height={14}
                                      style={{ color: COLORS.primary }}
                                    />
                                  </Box>
                                </Tooltip>
                              )}
                              <Box component="span" sx={styles.messageMetaInline}>
                                {msg.isEdited && !msg.isRevoked && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '11px',
                                      color: 'rgba(0,0,0,0.4)',
                                      fontStyle: 'italic',
                                      ml: 0.5,
                                    }}
                                  >
                                    (edited)
                                  </Typography>
                                )}
                                {msg.isStarred && !msg.isRevoked && (
                                  <Tooltip title="Starred" arrow>
                                    <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}
                                      aria-label="Starred message"
                                    >
                                      <Icon icon="mdi:star" width={12} height={12} style={{ color: COLORS.warning }} />
                                    </Box>
                                  </Tooltip>
                                )}
                                {isAiSent && !isFirstInGroup && (
                                  <Tooltip title="Written and sent by the AI Bot" arrow>
                                    <Box
                                      component="span"
                                      aria-label="Sent by the AI Bot"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                        ml: 0.5,
                                        px: '5px',
                                        height: 16,
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(99, 102, 241, 0.14)',
                                        color: '#4338CA',
                                        fontSize: '9px',
                                        fontWeight: 700,
                                        letterSpacing: '0.4px',
                                      }}
                                    >
                                      <Icon icon="solar:magic-stick-3-bold-duotone" width={10} height={10} />
                                      AI
                                    </Box>
                                  </Tooltip>
                                )}
                                {msg.timestamp && (
                                  <Typography component="span" sx={styles.messageTimeInline}>
                                    {msg.timestamp}
                                  </Typography>
                                )}
                                {msg.type === 'sent' && (
                                  <StatusIcon
                                    status={msg.status}
                                    updatedTimestamp={msg.updatedTimestamp}
                                    metaErrorCode={msg.meta_error_code}
                                    error={msg.error}
                                  />
                                )}
                                {/* On the reply, not above the composer. See
                                    MessageRating for why that mattered. */}
                                {/* Not on a template. The super-admin screen
                                    already settled this — "an outbound template
                                    has no reply to be right or wrong" — and
                                    asking somebody to grade wording the practice
                                    wrote and Meta approved collects a verdict
                                    about the wrong thing. */}
                                {isAiSent && !msg.aiTemplate && !isTestMode && msg.aiRating?.turn_id && (
                                  <Box component="span" sx={{ ml: 0.5, display: 'inline-flex' }}>
                                    <MessageRating
                                      turnId={msg.aiRating.turn_id}
                                      mine={msg.aiRating.mine}
                                      tagOptions={aiComposer?.ratingTags || []}
                                      replyText={msg.content || ''}
                                      onRate={handleRateMessage}
                                    />
                                  </Box>
                                )}
                              </Box>
                              {msg.emoji && <Box sx={styles.reaction}>{msg.emoji}</Box>}
                            </Box>
                          </Box>
                        </Box>

                        {/* Interactive Message Data Pills */}
                        {msg?.isMessageInteractive && (
                          <Box sx={styles.dataPills}>
                            {msg?.flowResponseEmail && (
                              <Box sx={{ ...styles.dataPill, ...styles.dataPillEmail }}>
                                <Icon icon="mdi:email-outline" width={14} height={14} />
                                {msg?.flowResponseEmail}
                              </Box>
                            )}
                            {msg?.flowResponseTreatment && (
                              <Box sx={{ ...styles.dataPill, ...styles.dataPillTreatment }}>
                                <Icon icon="mdi:tooth-outline" width={14} height={14} />
                                {msg?.flowResponseTreatment}
                              </Box>
                            )}
                            {msg?.flowResponseComment && (
                              <Box sx={{ ...styles.dataPill, ...styles.dataPillComment }}>
                                <Icon icon="mdi:comment-outline" width={14} height={14} />
                                {msg?.flowResponseComment}
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Tier 3 / F-33 — first-unread divider.
                                            Rendered between the message and the date divider in
                                            JSX; column-reverse places it visually above the
                                            boundary message and below the date label. */}
                        {index === unreadDividerIndex && unreadDividerCount > 0 && (
                          <UnreadDivider ref={unreadDividerCallbackRef} count={unreadDividerCount} />
                        )}
                        {/* Date Divider - Rendered after message, appears above due to column-reverse */}
                        {dateDivider && (
                          <Box sx={styles.dateDivider}>
                            <Box component="span" sx={styles.dateDividerText}>
                              {dateDivider}
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
                </InfiniteScroll>
              )}
            </Box>
          )}

          {/* Attachment preview — shown as outgoing chat bubble before sending */}
          {attachmentFile && (
            <Box sx={{ padding: '8px 16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={styles.attachmentPreviewBubble}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
                  <Icon icon="mdi:attachment" width={14} height={14} style={{ color: COLORS.primary }} />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {attachmentFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: COLORS.textDisabled, flexShrink: 0, ml: 'auto' }}>
                    {(attachmentFile.size / 1024).toFixed(0)} KB
                  </Typography>
                </Box>
                {attachmentPreview && attachmentFile.type?.startsWith('image/') && (
                  <Box
                    component="img"
                    src={attachmentPreview}
                    alt={attachmentFile.name}
                    sx={styles.attachmentThumbnail}
                  />
                )}
                {attachmentPreview && attachmentFile.type?.startsWith('video/') && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      p: '8px',
                      backgroundColor: COLORS.dividerLighter,
                      borderRadius: '6px',
                    }}
                  >
                    <Icon icon="mdi:video-outline" width={20} height={20} style={{ color: COLORS.textSecondary }} />
                    <Typography sx={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      {attachmentFile.name}
                    </Typography>
                  </Box>
                )}
                {!attachmentPreview && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      p: '8px',
                      backgroundColor: COLORS.dividerLighter,
                      borderRadius: '6px',
                    }}
                  >
                    <Icon icon="mdi:file-outline" width={20} height={20} style={{ color: COLORS.textSecondary }} />
                    <Typography sx={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      {attachmentFile.name}
                    </Typography>
                  </Box>
                )}
                <Box
                  component="input"
                  placeholder="Add a caption..."
                  value={attachmentCaption}
                  onChange={(e) => setAttachmentCaption(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendAttachment();
                    }
                  }}
                  sx={styles.attachmentCaptionInput}
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${COLORS.dividerMedium}`,
                    pt: '8px',
                    mt: '8px',
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleCancelAttachment}
                    sx={{ textTransform: 'none', fontSize: '12px', color: COLORS.textSecondary, minWidth: 'auto' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSendAttachment}
                    disabled={whatsppSendLoading || !isSendAllowed || isWindowExpired}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      backgroundColor: COLORS.primary,
                      '&:hover': { backgroundColor: COLORS.primaryDark },
                      minWidth: 'auto',
                      padding: '4px 14px',
                    }}
                  >
                    {whatsppSendLoading ? 'Sending...' : 'Send'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Hidden file input for attachments. The `accept` attribute is
                re-scoped at click time by triggerFilePicker() based on which
                option the user chose in the + popover (Photos/Videos vs
                Document). Default accept matches the union of both. */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,video/mp4,video/3gpp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Attachment "+" popover — native-WA-style coloured shortcuts.
                Only renders options whose underlying send path is wired up.
                Currently: Photos & Videos (images+video) and Document. */}
          <Popover
            open={Boolean(attachmentMenuAnchor)}
            anchorEl={attachmentMenuAnchor}
            onClose={closeAttachmentMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { sx: { ...styles.attachmentPopoverPaper, mt: '-8px' } } }}
          >
            <Box sx={{ py: 0.5 }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => triggerFilePicker('image/jpeg,image/png,video/mp4,video/3gpp')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    triggerFilePicker('image/jpeg,image/png,video/mp4,video/3gpp');
                }}
                sx={styles.attachmentPopoverItem}
              >
                <Box sx={{ ...styles.attachmentPopoverIconWrap, backgroundColor: '#3B82F6' }}>
                  <Icon icon="mdi:image-outline" width={20} height={20} />
                </Box>
                <Box sx={styles.attachmentPopoverTextCol}>
                  <Typography sx={styles.attachmentPopoverLabel}>Photos &amp; Videos</Typography>
                  <Typography sx={styles.attachmentPopoverSublabel}>JPEG, PNG, MP4</Typography>
                </Box>
              </Box>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => triggerFilePicker('.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    triggerFilePicker('.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt');
                }}
                sx={styles.attachmentPopoverItem}
              >
                <Box sx={{ ...styles.attachmentPopoverIconWrap, backgroundColor: '#A855F7' }}>
                  <Icon icon="mdi:file-document-outline" width={20} height={20} />
                </Box>
                <Box sx={styles.attachmentPopoverTextCol}>
                  <Typography sx={styles.attachmentPopoverLabel}>Document</Typography>
                  <Typography sx={styles.attachmentPopoverSublabel}>PDF, DOC, XLS, PPT, TXT</Typography>
                </Box>
              </Box>
            </Box>
          </Popover>

          {/* Closed-window indicator — single concise line above the closed-mode
              input. The fuller explanation lives in the info tooltip. */}
          {isWindowExpired && (
            <Box
              sx={{
                ...styles.windowStatus,
                ...styles.windowStatusExpired,
                borderTop: `1px solid ${COLORS.divider}`,
                marginBottom: 0,
                padding: '6px 16px',
              }}
            >
              <Icon icon="mdi:clock-outline" width={14} height={14} />
              <Typography component="span" sx={{ fontSize: '11px' }}>
                {personalReplyActive
                  ? 'Personal Reply — 24-hour window closed'
                  : 'Window closed - templates only.'}
              </Typography>
              {/* Info tooltip only when Personal Reply is active (it explains the
                  greeting/sign-off flow). */}
              {personalReplyActive && (
                <Tooltip
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  title="WhatsApp only lets you reply freely within 24 hours of the patient's last message. That window's closed, so you can only send an approved template until they reply — pick a greeting and sign-off, type your message, and we'll send it as an approved WhatsApp message."
                >
                  <IconButton
                    size="small"
                    aria-label="How Personal Reply works"
                    sx={{ color: 'inherit', p: '2px' }}
                  >
                    <Icon icon="mdi:information-outline" width={14} height={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Scroll to bottom FAB — rendered as a sibling of the footer so
                it anchors to the ChatPage root's relative box and hovers at
                the bottom-right of the chat pane (native-WA / iMessage feel). */}
          {/* Hidden while the footer is expanded (mapped-template draft bar or
              Personal Reply greeting/sign-off chips) — those add rows that make
              the footer taller than the FAB's fixed bottom offset, so the FAB
              would overlap the composer. It reappears once the footer collapses.
              Gate logic extracted to isScrollToBottomFabVisible (unit-tested). */}
          {isScrollToBottomFabVisible({
            isUserNearBottom,
            isLoading: whatsappChatScreenLoading,
            messageCount: whatsappMessages?.length,
            mappedTemplateDraft,
            personalReplyActive,
          }) && (
            <Box
              onClick={scrollToBottom}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') scrollToBottom();
              }}
              role="button"
              tabIndex={0}
              aria-label={
                unreadWhileScrolled > 0 ? `Scroll to latest — ${unreadWhileScrolled} new` : 'Scroll to latest'
              }
              sx={styles.scrollToBottomBtn}
            >
              <Icon icon="mdi:chevron-down" width={22} height={22} />
              {unreadWhileScrolled > 0 && (
                <Box component="span" sx={styles.scrollToBottomBtnBadge}>
                  {unreadWhileScrolled > 99 ? '99+' : unreadWhileScrolled}
                </Box>
              )}
            </Box>
          )}

          {/* Mapped-template send bar — shown whenever a checklist WhatsApp item
              has preloaded an approved template (draft bubble above). Works open
              OR closed: send goes via the approved-template path, never free text.
              Three distinct states, driven by `draft.status` (never conflated —
              a fetch that's still loading or that failed must NOT be reported as
              "missing details", or the banner lies about why Send is disabled):
                - loading — neutral "Checking details…", Send disabled.
                - error   — the practice/lead context fetch failed; distinct
                            message + Retry, Send disabled. Never "missing".
                - ready + missing fields — the real BLOCKED warning, names the
                  missing fields, Send disabled — never silently sends blanks.
                - ready + nothing missing — green "ready to send".
              Dismiss falls back to the normal composer in every state. */}
          {mappedTemplateDraft && (() => {
            const { blocked, status } = mappedTemplateDraft;
            const isLoadingContext = status === 'loading';
            const isContextError = status === 'error';
            const missingLabel = (mappedTemplateDraft.missingFields || [])
              .map((f) => MAPPED_FIELD_LABELS[f] || f)
              .join(', ');
            let bannerText = 'Template ready — review the draft above, then send.';
            let bannerIcon = 'ic:baseline-whatsapp';
            let bannerColor = '#25d366';
            let bannerTextColor = '#3b5c4a';
            let bannerBg = '#f0faf4';
            if (isLoadingContext) {
              bannerText = 'Checking lead and practice details…';
              bannerIcon = 'eos-icons:loading';
              bannerColor = '#637381';
              bannerTextColor = '#454f5b';
              bannerBg = '#f4f6f8';
            } else if (isContextError) {
              bannerText = "Couldn't load practice/lead details — try again before sending.";
              bannerIcon = 'eva:alert-circle-outline';
              bannerColor = '#b71d18';
              bannerTextColor = '#7a1712';
              bannerBg = '#fdeceb';
            } else if (blocked) {
              bannerText = `Missing details for this template: ${missingLabel}. Add them to the lead before sending.`;
              bannerIcon = 'eva:alert-triangle-outline';
              bannerColor = '#b76e00';
              bannerTextColor = '#7a4f00';
              bannerBg = '#fff6e5';
            }
            return (
              // Stacked layout: message row on top, button row below. Stays tidy
              // at narrow follow-up-sidebar widths (buttons flex to fill / wrap)
              // and reads fine on wide screens. Compact vertical rhythm.
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${COLORS.divider}`,
                  backgroundColor: bannerBg,
                }}
              >
                {/* Row 1 — status message (icon + wrapping text, full width). */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Icon
                    icon={bannerIcon}
                    width={18}
                    height={18}
                    style={{ color: bannerColor, flexShrink: 0, marginTop: 1 }}
                  />
                  <Typography sx={{ fontSize: '12px', color: bannerTextColor, flexGrow: 1, minWidth: 0 }}>
                    {bannerText}
                  </Typography>
                </Box>
                {/* Row 2 — actions. Right-aligned; on a tight sidebar they flex to
                    fill and wrap rather than overflow. */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    onClick={handleDismissMappedTemplate}
                    sx={{ color: '#637381', textTransform: 'none', flex: '1 1 auto', minWidth: 96, maxWidth: 160 }}
                  >
                    Dismiss
                  </Button>
                  {isContextError && (
                    <Button
                      size="small"
                      onClick={mappedTemplate.retryContext}
                      sx={{ color: '#b71d18', textTransform: 'none', flex: '1 1 auto', minWidth: 96, maxWidth: 160 }}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleMappedTemplateSend}
                    disabled={blocked || isComposerDisabled}
                    startIcon={<Icon icon="mdi:send" width={15} height={15} />}
                    sx={{
                      backgroundColor: '#25d366',
                      '&:hover': { backgroundColor: '#1eb457' },
                      textTransform: 'none',
                      flex: '1 1 auto',
                      minWidth: 130,
                      maxWidth: 200,
                    }}
                  >
                    Send template
                  </Button>
                </Box>
              </Box>
            );
          })()}

          {/* Footer */}
          <Box sx={{ ...styles.footer, ...(isMobile && styles.footerMobile) }}>
            {isWindowExpired ? (
              <>
                {/* Closed-window composer — "+"-menu driven. Greeting/sign-off
                    chips show only while Personal Reply is active; the "+" button
                    (left of the input) re-opens Personal Reply or opens the native
                    Templates picker. Never a free-text send. The chips animate open
                    /closed (Collapse) so the footer doesn't hard-snap on send. */}
                <Collapse in={personalReplyActive} timeout={180}>
                  <Box>
                    {/* Greeting row ALWAYS renders (all 3 chips) for a consistent
                        UX regardless of Meta's rolling per-template approval. Not-
                        yet-approved greetings are disabled/greyed with an
                        "awaiting approval" tooltip; the active greeting is always
                        an approved one (usePersonalReply.pickAvailableGreeting). */}
                    <>
                      <Typography sx={styles.personalReplyRowLabel}>Greeting</Typography>
                      <Box sx={styles.personalReplyChipRow}>
                        {personalReply.greetingOptions.map((greeting) => (
                          <QuickReplyChip
                            key={greeting.key}
                            label={greeting.label}
                            active={greeting.key === personalReply.activeGreeting}
                            disabled={!greeting.available}
                            tooltip={
                              greeting.available
                                ? ''
                                : "This greeting isn't approved for this sign-off yet"
                            }
                            onSelect={() => personalReply.setGreeting(greeting.key)}
                          />
                        ))}
                      </Box>
                    </>
                    <Typography sx={styles.personalReplyRowLabel}>Sign-off</Typography>
                    <Box sx={styles.personalReplyChipRow}>
                      {personalReply.signOffOptions.map((option) => (
                        <QuickReplyChip
                          key={option.key}
                          label={option.label}
                          active={option.key === personalReply.activeSignOff}
                          disabled={!option.group}
                          onSelect={() => personalReply.setSignOff(option.key)}
                        />
                      ))}
                    </Box>
                  </Box>
                </Collapse>
                <Box sx={styles.inputContainer}>
                  {/* "+" menu launcher — same styling as the open-window tools
                      button, but its menu holds only Personal Reply + Templates. */}
                  <Tooltip title="Reply options" arrow>
                    <IconButton
                      onClick={(evt) => setClosedToolsAnchor(evt.currentTarget)}
                      disabled={isComposerDisabled}
                      sx={styles.inputBtn}
                      aria-label="Reply options"
                    >
                      <Icon icon="mdi:plus" width={22} height={22} />
                    </IconButton>
                  </Tooltip>
                  <textarea
                    value={sendMessage}
                    rows={1}
                    placeholder={
                      personalReplyActive
                        ? 'Type your personalised message…'
                        : 'Tap + to reply with a template or Personal Reply'
                    }
                    style={{
                      ...styles.inputBox,
                      ...(!personalReplyActive || isComposerDisabled
                        ? { opacity: 0.5, cursor: 'not-allowed' }
                        : {}),
                    }}
                    onChange={handleSendMessageChange}
                    disabled={!personalReplyActive || isComposerDisabled}
                    onKeyDown={(evt) => {
                      // Enter sends the resolved template; Shift+Enter is a no-op
                      // (newlines are invalid in a template parameter anyway).
                      if (evt.key === 'Enter' && !evt.repeat && !evt.nativeEvent?.isComposing) {
                        evt.preventDefault();
                        if (!evt.shiftKey) handlePersonalReplySend();
                      }
                    }}
                  />
                  <IconButton
                    onClick={handlePersonalReplySend}
                    sx={{ ...styles.sendBtn, ...(!canSendPersonalReply ? styles.sendBtnDisabled : {}) }}
                    disabled={!canSendPersonalReply}
                    aria-label="Send Personal Reply"
                  >
                    <Icon icon="mdi:send" width={18} height={18} />
                  </IconButton>
                </Box>
                {personalReplyActive && sendMessage.trim() && personalReply.answerError && (
                  <Typography
                    role="alert"
                    sx={{ fontSize: '11px', lineHeight: 1.4, color: '#B71D18', mt: '6px', px: '4px' }}
                  >
                    {personalReply.answerError}
                  </Typography>
                )}
                {/* Closed-window "+" menu — exactly two items (no Attach/Formatting/
                    AI; those are invalid outside the 24h window). */}
                <Popover
                  open={Boolean(closedToolsAnchor)}
                  anchorEl={closedToolsAnchor}
                  onClose={() => setClosedToolsAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  slotProps={{ paper: { sx: styles.composerToolsPaper } }}
                >
                  <Box sx={{ py: 0.5 }}>
                    {/* Hide "Personal Reply" while a mapped-template draft is
                        pinned — personalReplyActive is suppressed by the draft, so
                        the item would be a dead click. */}
                    {showPersonalReplyMenuItem(personalReply.hasOptions, mappedTemplateDraft) && (
                      <MenuItem
                        onClick={() => {
                          setClosedToolsAnchor(null);
                          setPersonalReplyOpen(true);
                        }}
                        sx={styles.composerToolsItem}
                      >
                        <Icon icon="mdi:flash" width={18} height={18} />
                        Personal Reply
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        setClosedToolsAnchor(null);
                        setTemplateModalOpen(true);
                      }}
                      sx={styles.composerToolsItem}
                    >
                      <Icon icon="mdi:file-document-outline" width={18} height={18} />
                      Templates
                    </MenuItem>
                  </Box>
                </Popover>
              </>
            ) : (
              /* Normal Message Mode - disabled while loading */
              <>
                {/* AI Result Panel */}
                <FeatureGuard feature="whatsapp-ai" level="view">
                  <AIResultPanel
                    loading={aiAssistantLoading}
                    result={aiAssistantResult}
                    error={aiAssistantError}
                    actionType={aiAssistantActionType}
                    onClose={handleAiResultClose}
                    onSend={handleAiResultSend}
                    onRegenerate={handleAiRegenerate}
                  />
                </FeatureGuard>

                {/* Reply-to-quote preview — sits above the composer when the
                            operator picks "Reply" on a bubble. Mirrors native WA: a green
                            left-bar accent, sender label, single-line body preview, and a
                            close button that clears the active reply.
                            Track D / F9: framer-motion slide-up animation on appear/dismiss. */}
                <AnimatePresence initial={false}>
                  {replyingTo && (
                    <m.div
                      key="composer-quote-preview"
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 10, opacity: 0 }}
                      transition={{ duration: 0.15, ease: [0.2, 0.9, 0.3, 1] }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          margin: '6px 12px 0 12px',
                          padding: '8px 10px',
                          backgroundColor: COLORS.primaryLighter,
                          borderRadius: '8px',
                          borderLeft: `3px solid ${COLORS.primary}`,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: COLORS.primary,
                              lineHeight: 1.2,
                            }}
                          >
                            {replyingTo.isFromMe
                              ? 'You'
                              : whatsAppChatMetaData?.name || whatsAppChatMetaData?.phoneNumber || 'Contact'}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: COLORS.textSecondary,
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {replyingTo.body || 'Media'}
                          </Typography>
                        </Box>
                        <Tooltip title="Cancel reply" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setReplyingTo(null)}
                            sx={{
                              color: COLORS.textSecondary,
                              '&:hover': { color: COLORS.textPrimary },
                            }}
                          >
                            <Icon icon="mdi:close" width={16} height={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </m.div>
                  )}
                </AnimatePresence>
                {/* The rating panel that used to sit here is gone.
                
                    It rated only the LATEST turn while you were usually reading an
                    earlier one, and it cost about a third of the inbox permanently —
                    quoted text, two buttons, seven tag chips, a comment box and two
                    lines of help — whether or not anybody was rating anything.
                
                    The verdict now lives on each AI bubble (MessageRating), and the
                    tags and the note open in a dialog, so the rare case no longer
                    sets the size of the common one. `aiComposer.rate` and
                    `tag_options` are still the transport; only the surface changed. */}
                {/* AI is writing / AI wrote this. The status sits directly on
                    top of the box the words land in, so there is never a moment
                    where a reply is being prepared somewhere the operator is not
                    looking. */}
                <AnimatePresence initial={false}>
                  {(aiComposer?.composing || composerHoldsAiDraft) && (
                    <m.div
                      key="composer-ai-strip"
                      initial={{ y: 8, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 8, opacity: 0 }}
                      transition={{ duration: 0.15, ease: [0.2, 0.9, 0.3, 1] }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          margin: '6px 12px 0 12px',
                          padding: '6px 10px',
                          borderRadius: '8px',
                          borderLeft: '3px solid #6366F1',
                          backgroundColor: 'rgba(99, 102, 241, 0.08)',
                        }}
                      >
                        <Icon
                          icon={
                            aiComposer?.composing
                              ? 'svg-spinners:3-dots-fade'
                              : 'solar:magic-stick-3-bold-duotone'
                          }
                          width={16}
                          height={16}
                          style={{ color: '#4338CA', flexShrink: 0 }}
                        />
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#3730A3',
                            flexGrow: 1,
                            minWidth: 0,
                          }}
                        >
                          {aiComposer?.composing
                            ? 'AI Bot is writing a reply…'
                            : 'AI Bot wrote this — edit it and press Send, or discard it.'}
                        </Typography>
                        {composerHoldsAiDraft && (
                          <Tooltip title="Discard the AI's reply" arrow>
                            <IconButton
                              size="small"
                              onClick={discardAiDraft}
                              aria-label="Discard the AI draft"
                              sx={{ color: '#4338CA', padding: '2px' }}
                            >
                              <Icon icon="mdi:close" width={15} height={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </m.div>
                  )}
                </AnimatePresence>
                {aiComposer?.composing && (
                  <Box
                    sx={{
                      height: '2px',
                      margin: '4px 12px 0 12px',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      backgroundColor: 'rgba(99, 102, 241, 0.18)',
                      '@keyframes aiComposerSweep': {
                        '0%': { transform: 'translateX(-100%)' },
                        '100%': { transform: 'translateX(300%)' },
                      },
                      '&::after': {
                        content: '""',
                        display: 'block',
                        width: '33%',
                        height: '100%',
                        backgroundColor: '#6366F1',
                        animation: 'aiComposerSweep 1.1s ease-in-out infinite',
                      },
                    }}
                  />
                )}
                <Box sx={embedded ? styles.embeddedInputContainer : styles.inputContainer}>
                  {isMobile && !embedded ? (
                    <Box sx={styles.composerPrimaryRow}>
                      <Tooltip title="Message tools" arrow>
                        <IconButton
                          onClick={openComposerTools}
                          disabled={isComposerDisabled}
                          sx={styles.inputBtn}
                          aria-label="Message tools"
                        >
                          <Icon icon="mdi:plus" width={22} height={22} />
                        </IconButton>
                      </Tooltip>
                      <textarea
                        ref={inputRef}
                        value={sendMessage}
                        rows={1}
                        placeholder={(() => {
                          if (!isSendAllowed)
                            return threadHasLead && !leadDetailsLoaded
                              ? 'Loading lead details...'
                              : 'Lead is not available for communication';
                          if (whatsppSendLoading || aiDraftSending) return 'Sending...';
                          if (whatsappChatScreenLoading) return 'Loading...';
                          if (aiComposer?.composing) return 'AI Bot is writing a reply…';
                          return 'Type your message...';
                        })()}
                        style={{
                          ...styles.inputBox,
                          ...(isComposerDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                        }}
                        onChange={handleSendMessageChange}
                        onKeyUp={(evt) => {
                          composerAutocomplete.evaluate(sendMessage, evt?.target?.selectionStart ?? sendMessage.length);
                        }}
                        disabled={isComposerDisabled}
                        onKeyDown={(evt) => {
                          if (handleAutocompleteKeyDown(evt)) return;
                          if (evt.key === 'Enter' && !evt.shiftKey && !evt.repeat && !evt.nativeEvent?.isComposing) {
                            evt.preventDefault();
                            sendWhatsappMessageHandler(sendMessage);
                            return;
                          }
                          if (evt.key === 'Enter' && !evt.shiftKey) {
                            evt.preventDefault();
                            return;
                          }
                          const isMod = evt.metaKey || evt.ctrlKey;
                          if (isMod && evt.key === 'b') {
                            evt.preventDefault();
                            applyFormatting('*');
                          } else if (isMod && evt.key === 'i') {
                            evt.preventDefault();
                            applyFormatting('_');
                          } else if (isMod && evt.shiftKey && (evt.key === 'x' || evt.key === 'X')) {
                            evt.preventDefault();
                            applyFormatting('~');
                          } else if (isMod && evt.shiftKey && (evt.key === 'm' || evt.key === 'M')) {
                            evt.preventDefault();
                            applyFormatting('```');
                          }
                        }}
                        onPaste={handleComposerPaste}
                      />
                      <EmojiPickerButton onEmojiSelect={handleEmojiSelect} disabled={isComposerDisabled} isMobile={isMobile} />
                      <IconButton
                        onClick={() => sendWhatsappMessageHandler(sendMessage)}
                        sx={{
                          ...styles.sendBtn,
                          ...(!canSendComposerMessage || aiDraftSending ? styles.sendBtnDisabled : {}),
                        }}
                        disabled={!canSendComposerMessage || aiDraftSending}
                        aria-label="Send WhatsApp message"
                      >
                        <Icon icon="mdi:send" width={18} height={18} />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      {/* Desktop layout */}
                      <Tooltip title="Message tools" arrow>
                        <IconButton
                          onClick={openComposerTools}
                          disabled={isComposerDisabled}
                          sx={styles.inputBtn}
                          aria-label="Message tools"
                        >
                          <Icon icon={embedded ? 'mdi:dots-horizontal' : 'mdi:plus'} width={22} height={22} />
                        </IconButton>
                      </Tooltip>
                      <textarea
                        ref={inputRef}
                        value={sendMessage}
                        rows={1}
                        placeholder={(() => {
                          if (!isSendAllowed)
                            return threadHasLead && !leadDetailsLoaded
                              ? 'Loading lead details...'
                              : 'Lead is not available for communication';
                          if (whatsppSendLoading || aiDraftSending) return 'Sending...';
                          if (whatsappChatScreenLoading) return 'Loading...';
                          if (aiComposer?.composing) return 'AI Bot is writing a reply…';
                          return 'Type your message...';
                        })()}
                        style={{
                          ...styles.inputBox,
                          ...(isComposerDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                        }}
                        onChange={handleSendMessageChange}
                        onKeyUp={(evt) => {
                          composerAutocomplete.evaluate(sendMessage, evt?.target?.selectionStart ?? sendMessage.length);
                        }}
                        disabled={isComposerDisabled}
                        onKeyDown={(evt) => {
                          if (handleAutocompleteKeyDown(evt)) return;
                          // 2026-05-06 P0 — repeat / IME Enter must not fire send.
                          if (evt.key === 'Enter' && !evt.shiftKey && !evt.repeat && !evt.nativeEvent?.isComposing) {
                            evt.preventDefault();
                            sendWhatsappMessageHandler(sendMessage);
                            return;
                          }
                          if (evt.key === 'Enter' && !evt.shiftKey) {
                            evt.preventDefault();
                            return;
                          }
                          const isMod = evt.metaKey || evt.ctrlKey;
                          if (isMod && evt.key === 'b') {
                            evt.preventDefault();
                            applyFormatting('*');
                          } else if (isMod && evt.key === 'i') {
                            evt.preventDefault();
                            applyFormatting('_');
                          } else if (isMod && evt.shiftKey && (evt.key === 'x' || evt.key === 'X')) {
                            evt.preventDefault();
                            applyFormatting('~');
                          } else if (isMod && evt.shiftKey && (evt.key === 'm' || evt.key === 'M')) {
                            evt.preventDefault();
                            applyFormatting('```');
                          }
                        }}
                        onPaste={(evt) => {
                          // F-17 — see desktop variant. Image consumed by the
                          // helper if present; plain text falls through to the
                          // textarea's native paste so undo history + a11y hooks
                          // keep working.
                          handleComposerPaste(evt);
                        }}
                      />
                      <EmojiPickerButton onEmojiSelect={handleEmojiSelect} disabled={isComposerDisabled} />
                      <IconButton
                        onClick={() => sendWhatsappMessageHandler(sendMessage)}
                        sx={{
                          ...styles.sendBtn,
                          ...(!canSendComposerMessage || aiDraftSending ? styles.sendBtnDisabled : {}),
                        }}
                        disabled={!canSendComposerMessage || aiDraftSending}
                        aria-label="Send WhatsApp message"
                      >
                        <Icon icon="mdi:send" width={18} height={18} />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Popover
                  open={Boolean(composerToolsAnchor)}
                  anchorEl={composerToolsAnchor}
                  onClose={closeComposerTools}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  slotProps={{ paper: { sx: styles.composerToolsPaper } }}
                >
                  <Box sx={{ py: 0.5 }}>
                    <MenuItem
                      onClick={() => {
                        closeComposerTools();
                        setTemplateModalOpen(true);
                      }}
                      disabled={isComposerDisabled}
                      sx={styles.composerToolsItem}
                    >
                      <Icon icon="mdi:file-document-outline" width={18} height={18} />
                      Templates
                    </MenuItem>
                    <MenuItem
                      onClick={openAttachmentFromComposerTools}
                      disabled={isComposerDisabled}
                      sx={styles.composerToolsItem}
                    >
                      <Icon icon="mdi:paperclip" width={18} height={18} />
                      Attach file
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={styles.composerFormatSection}>
                      <Typography sx={styles.composerToolsLabel}>Formatting</Typography>
                      <FormattingToolbar
                        textareaRef={inputRef}
                        value={sendMessage}
                        onChange={setSendMessage}
                        isCompact={isCompact || isMobile || embedded}
                      />
                    </Box>
                    {isAiEnabled && (
                      <FeatureGuard feature="whatsapp-ai" level="view">
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem
                          onClick={openAiFromComposerTools}
                          disabled={isComposerDisabled || aiAssistantLoading}
                          sx={styles.composerToolsItem}
                        >
                          <Icon icon="mdi:sparkles" width={18} height={18} />
                          AI assist
                        </MenuItem>
                      </FeatureGuard>
                    )}
                  </Box>
                </Popover>

                {/* AI Assistant Menu */}
                {isAiEnabled && (
                  <FeatureGuard feature="whatsapp-ai" level="view">
                    <AIAssistantMenu
                      anchorEl={aiMenuAnchor}
                      open={aiMenuOpen}
                      onClose={handleAiMenuClose}
                      onSelectAction={handleAiSelectAction}
                      loading={aiAssistantLoading}
                      isMobile={isMobile}
                    />
                  </FeatureGuard>
                )}

                {/* Tier 2 / F-20, F-21, F-22 — Composer autocomplete popover.
                            Anchored to the textarea; one component handles all three
                            triggers because the items / labels / selection logic is
                            piped in from the trigger-specific memos above. */}
                <ComposerAutocompletePopover
                  open={Boolean(composerAutocomplete.trigger) && activeAutocompleteItems.length > 0}
                  anchorEl={inputRef.current}
                  items={activeAutocompleteItems}
                  selectedIndex={composerAutocomplete.selectedIndex}
                  onIndexChange={composerAutocomplete.setSelectedIndex}
                  onSelect={(item) => applyAutocompleteSelection(item)}
                  onClose={composerAutocomplete.close}
                  getKey={(item, idx) => {
                    if (composerAutocomplete.trigger === '/') return item?.id ?? item?.name ?? idx;
                    if (composerAutocomplete.trigger === ':') return item?.emoji + idx;
                    return item;
                  }}
                  getLabel={renderAutocompleteLabel}
                  getDescription={renderAutocompleteDescription}
                  placement="top-start"
                  emptyLabel={(() => {
                    if (composerAutocomplete.trigger === '/') return 'No matching templates';
                    if (composerAutocomplete.trigger === ':') return 'No matching emoji';
                    return 'No matching merge tags';
                  })()}
                  footerAction={
                    composerAutocomplete.trigger === '/'
                      ? {
                          label: 'Browse templates',
                          icon: 'mdi:file-document-multiple-outline',
                          onClick: () => {
                            composerAutocomplete.close();
                            setTemplateModalOpen(true);
                          },
                        }
                      : null
                  }
                />
              </>
            )}
          </Box>
        </>
      }

      {/* Template Modal */}
      <WhatsappTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        currentIndex={currentIndex}
        templates={templatesForPicker}
        onNext={() => setCurrentIndex((i) => Math.min(i + 1, templatesForPicker.length - 1))}
        onPrevious={() => setCurrentIndex((i) => Math.max(i - 1, 0))}
        onSelect={setCurrentIndex}
        onSend={() => sendWhatsappMessageTemplate(templatesForPicker[currentIndex] ?? {})}
        providerAccount={{
          id: currentBusinessId,
          is_connected: currentBusinessIsConnected,
        }}
        // Same resolution this file already uses at the lead-detail drawer and
        // the send call — the picker needs it to check audience rules.
        leadId={whatsAppChatMetaData?.leadDetails?.id || whatsAppChatMetaData?.thread?.lead?.id || null}
      />

      {/* Close Conversation Popup */}
      <CloseChatPopup
        open={openCloseConversation}
        onClose={handleCancelCloseConversation}
        onConfirmClose={handleConfirmCloseConversation}
        contactDetails={{
          name: whatsAppChatMetaData?.thread?.contact_name,
          leadDetails: whatsAppChatMetaData?.leadDetails,
        }}
        practiceId={practiceId}
        practiceChoices={practiceChoices}
      />

      {/* Bubble action sheet — Reply + Copy + Forward today. New
                Tier-1 features (react, star, edit, pin, delete, info) plug
                in via the `slots` prop without rewiring the trigger logic.
                Track C: Edit visible only when the message is outbound text
                inside the 15-minute Meta window (D8 — silent gating, no
                countdown banner). Info visible on every eligible bubble. */}
      <MessageActionSheet
        open={Boolean(bubbleMenu)}
        anchorEl={bubbleMenu?.anchorEl ?? null}
        message={bubbleMenu?.msg ?? null}
        onClose={closeBubbleMenu}
        slots={{
          reactionBar: bubbleMenu?.msg ? (
            <EmojiReactionBar
              currentEmoji={bubbleMenu?.msg?.emoji || null}
              onPick={handleReactionPick}
              onRemove={handleReactionRemove}
            />
          ) : null,
          primaryActions: [
            <MenuItem
              key="reply"
              role="menuitem"
              onClick={() => handleReplyBubble()}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:reply-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Reply
            </MenuItem>,
            <MenuItem
              key="copy"
              role="menuitem"
              onClick={handleCopyBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:content-copy" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Copy
            </MenuItem>,
            <MenuItem
              key="forward"
              role="menuitem"
              onClick={handleForwardBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:share-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Forward
            </MenuItem>,
            // Edit only renders for outbound messages within the
            // 15-min Meta edit window AND only when not in
            // multi-select mode (single-bubble action only).
            !isSelecting && isBubbleEditEligible(bubbleMenu?.msg) ? (
              <MenuItem
                key="edit"
                role="menuitem"
                onClick={handleEditBubble}
                sx={{
                  fontSize: '13px',
                  py: 0.9,
                  px: 1.75,
                  gap: 1.25,
                  color: COLORS.textPrimary,
                  '&:hover': { backgroundColor: COLORS.primaryLighter },
                }}
              >
                <Icon icon="mdi:pencil-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
                Edit
              </MenuItem>
            ) : null,
          ],
          organiseActions: [
            <MenuItem
              key="star"
              role="menuitem"
              onClick={handleStarBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon
                icon={bubbleMenu?.msg?.isStarred ? 'mdi:star' : 'mdi:star-outline'}
                width={16}
                height={16}
                style={{ color: bubbleMenu?.msg?.isStarred ? COLORS.warning : COLORS.textSecondary }}
              />
              {bubbleMenu?.msg?.isStarred ? 'Unstar' : 'Star'}
            </MenuItem>,
            <MenuItem
              key="pin"
              role="menuitem"
              onClick={handlePinBubble}
              disabled={Boolean(bubbleMenu?.msg?.isRevoked)}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:pin-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Pin
            </MenuItem>,
            <MenuItem
              key="select"
              role="menuitem"
              onClick={() => {
                const msg = bubbleMenu?.msg;
                closeBubbleMenu();
                if (msg?.id != null && isBubbleActionEligible(msg)) toggleSelect(msg.id);
              }}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon
                icon="mdi:checkbox-multiple-marked-outline"
                width={16}
                height={16}
                style={{ color: COLORS.textSecondary }}
              />
              Select
            </MenuItem>,
          ],
          detailActions: [
            <MenuItem
              key="info"
              role="menuitem"
              onClick={handleInfoBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:information-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Info
            </MenuItem>,
          ],
          destructiveActions:
            bubbleMenu?.msg?.type === 'sent'
              ? [
                  <MenuItem
                    key="delete"
                    role="menuitem"
                    onClick={handleDeleteBubble}
                    sx={{
                      fontSize: '13px',
                      py: 0.9,
                      px: 1.75,
                      gap: 1.25,
                      color: COLORS.error || '#d32f2f',
                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                    }}
                  >
                    <Icon
                      icon="mdi:delete-outline"
                      width={16}
                      height={16}
                      style={{ color: COLORS.error || '#d32f2f' }}
                    />
                    Delete from CRM
                  </MenuItem>,
                ]
              : null,
        }}
      />

      {/* Delete-for-everyone confirmation dialog. Native WA shows an
                inline action sheet; we use MUI Dialog to match the rest of the
                CRM and so destructive intent is unmistakable. */}
      <Dialog
        open={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: 16 }}>Delete from CRM?</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: COLORS.textBody, mb: 1 }}>
            This message will be hidden in the CRM and replaced with a &ldquo;deleted&rdquo; placeholder. This cannot be
            undone.
          </Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.textMuted || 'rgba(0,0,0,0.55)', fontStyle: 'italic' }}>
            Note: WhatsApp&rsquo;s Cloud API does not currently expose delete-for-everyone. The patient&rsquo;s phone
            will still show the original message.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeTarget(null)} sx={{ fontSize: 13 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRevoke} color="error" variant="contained" sx={{ fontSize: 13 }}>
            Delete from CRM
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog for replacing an existing pin (D5 — max 1
                pin per thread; new pin overwrites old). */}
      <Dialog
        open={Boolean(replacePinTarget)}
        onClose={() => setReplacePinTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: 16 }}>Replace pinned message?</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: COLORS.textBody }}>
            Only one message can be pinned at a time. Pinning this message will replace the current pin.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setReplacePinTarget(null)}
            size="small"
            sx={{ textTransform: 'none', color: COLORS.textSecondary }}
          >
            Cancel
          </Button>
          <Button onClick={confirmReplacePin} size="small" variant="contained" sx={{ textTransform: 'none' }}>
            Replace
          </Button>
        </DialogActions>
      </Dialog>

      {/* Starred messages drawer — opens from the thread header
                button. Per D4 the drawer carries a "This chat" / "All chats"
                toggle; cross-thread is BE-deferred. */}
      <StarredMessagesPanel
        open={starredPanelOpen}
        onClose={() => setStarredPanelOpen(false)}
        threadId={whatsAppChatMetaData?.thread?.id}
      />

      {/* Track C / F8 — Message Info drawer. */}
      <MessageInfoDrawer
        open={Boolean(messageInfoTarget)}
        onClose={() => setMessageInfoTarget(null)}
        threadId={messageInfoTarget?.threadId ?? null}
        messageId={messageInfoTarget?.messageId ?? null}
      />

      {/* Track D / F3 — Forward picker. Single-target per D11. */}
      <ForwardPickerDialog
        open={forwardPicker.open}
        count={forwardPicker.ids.length}
        currentThreadId={whatsAppChatMetaData?.thread?.id}
        onClose={closeForwardPicker}
        onConfirm={confirmForward}
      />

      {/* Service-window-closed dialog. Surfaces the backend's HTTP 409
                in a sticky surface (not a toast) because the operator needs
                the "switch to template" CTA without losing the typed draft. */}
      <Dialog
        open={Boolean(serviceWindowClosedDialog)}
        onClose={() => dispatch(clearServiceWindowClosedDialog())}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 16 }}>
          <Icon icon="mdi:clock-alert-outline" width={22} height={22} style={{ color: COLORS.warningDark }} />
          24-hour window closed
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: COLORS.textBody, mb: 1.5 }}>
            {serviceWindowClosedDialog?.error ||
              "Free-form WhatsApp messages can only be delivered within 24 hours of the contact's last inbound message."}
          </Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.textSecondary }}>
            Send an approved template instead — Meta will deliver it regardless of window state.
          </Typography>
          {serviceWindowClosedDialog?.draft && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1,
                backgroundColor: COLORS.backgroundLight,
                border: `1px solid ${COLORS.dividerLight}`,
                fontSize: 12,
                color: COLORS.textPrimary,
                whiteSpace: 'pre-wrap',
                maxHeight: 96,
                overflow: 'auto',
              }}
            >
              <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 0.5 }}>
                Your draft (preserved):
              </Typography>
              {serviceWindowClosedDialog.draft}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => dispatch(clearServiceWindowClosedDialog())}
            size="small"
            sx={{ textTransform: 'none', color: COLORS.textSecondary }}
          >
            Dismiss
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              dispatch(clearServiceWindowClosedDialog());
              setTemplateModalOpen(true);
            }}
            sx={{
              textTransform: 'none',
              backgroundColor: COLORS.primary,
              '&:hover': { backgroundColor: COLORS.primaryDark },
            }}
          >
            Switch to template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ChatPage.propTypes = {
  onToggleDetails: PropTypes.func,
  detailsOpen: PropTypes.bool,
  embedded: PropTypes.bool,
  onOpenBooking: PropTypes.func,
  onSetChatMessage: PropTypes.func,
  onOpenResolve: PropTypes.func,
  hideResolve: PropTypes.bool,
  onMessageSent: PropTypes.func,
  isMobile: PropTypes.bool,
  isCompact: PropTypes.bool,
  onBack: PropTypes.func,
  onToggleFullscreen: PropTypes.func,
  isFullscreen: PropTypes.bool,
  onToggleFocusMode: PropTypes.func,
  isFocusMode: PropTypes.bool,
  prefillTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefillPracticeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefillWaTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefillWaTemplateName: PropTypes.string,
  prefillWaItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMessageConfirmed: PropTypes.func,
};

ChatPage.defaultProps = {
  onToggleDetails: () => {},
  detailsOpen: false,
  embedded: false,
  onOpenBooking: undefined,
  onSetChatMessage: undefined,
  onOpenResolve: undefined,
  hideResolve: false,
  onMessageSent: undefined,
  isMobile: false,
  isCompact: false,
  onBack: undefined,
  onToggleFullscreen: undefined,
  isFullscreen: false,
  onToggleFocusMode: undefined,
  isFocusMode: false,
  prefillTemplateId: null,
  prefillPracticeId: null,
  prefillWaTemplateId: null,
  prefillWaTemplateName: null,
  prefillWaItemId: null,
  onMessageConfirmed: undefined,
};

export default ChatPage;
